import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { config } from '@/config'
import chevronDoubleRightIcon from '@/assets/icons/chevron-double-right.png'
import { MaterialIcon } from '@/ui/material-icon'
import { Loader } from '@/shared/components/Loader/Loader'
import {
  createAdpSourceDataSource,
  fetchAdpConnectionColumns,
  fetchAdpSourceFilesList,
  fetchAdpDqStatsBySourceFileId,
  postAdpDqDataProfiling,
  postAdpDqStats,
  postAdpSourceFiles,
  testAdpConnection,
  fetchAdpDataSourcesFilter,
  ADP_DATA_SOURCES_FILTER_COLUMN_SOURCE,
  resolveAdpStaticAssetUrl,
} from '@/features/adp/api/adpDataSources.api'
import {
  type PresignedUrlRequestBody,
  postFilesPresignedUrl,
  putFileToPresignedUrl,
  contentTypeForSourceFileName,
  postShowTablesAndCreateV2,
  parseExcelSheetsFromTablePaths,
} from '@/features/adp/api/akashicIngestion.api'
import { useJobMonitoringWebSocket } from '@/features/adp/hooks/useJobMonitoringWebSocket'

import type {
  AdpConnectionColumnItem,
  AdpConnectionColumnsDatabasePayload,
  AdpConnectionTestDatabasePayload,
  AdpConnectionTestOptionField,
  AdpDqStatRecord,
  AdpSourceDataSourceCreatePayload,
  AdpSourceFileListItem,
  AdpSourceFileRegisteredField,
} from '@/features/adp/api/adpDataSources.types'

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui/dialog'
import { Button } from '@/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/tooltip'
import styles from './ConfigureDataSourceSheet.module.scss'
import { FieldRow } from './FieldRow'
import { PrerequisitesModal } from '@/shared/components/PrerequisitesModal/PrerequisitesModal'

import {
  getSourceConfigDraft,
  readAdpPipelineSession,
  upsertAssetSession,
  upsertAssetProfilingSessionId,
  upsertSourceConfigDraft,
} from '@/features/adp/utils/adpPipelineSessionStorage'
import { toast } from 'sonner'
import {
  CUSTOM_FILE_DELIMITER,
  FILE_DELIMITER_CHOICES,
  isDelimitedTextFileName,
  resolveFileDelimiterForApi,
} from '@/features/adp/constants/fileDelimiters'
import { validateFormFields } from '@/features/adp/utils/validateSchemaField'
import { RegisteredSourcePickerItem } from '@/features/adp/hooks/useRegisteredDataSources'
import { DynamicConfigFormSkeleton } from '../shared/DynamicConfigFormSkeleton'
import {
  RegisteredSourceDetailsCard,
  type RegisteredExistingSourceSelection,
} from './RegisteredSourceDetailsCard'
import { getNotRegisteredTables } from '@/features/adp/services/api-service'

export type ConfigureDataSourceSheetProps = {
  onClose: () => void
  onBackToChoose: () => void

  /** Canvas node id — used to persist this asset in session storage for join/union/transform pickers. */
  configuringNodeId?: string | null
  sourceTitle?: string
  sourceTypeLabel?: string
  connectorImage?: string
  registeredSource?: RegisteredSourcePickerItem | null
  allowedInstance?: string | null
}

type FormState = Record<string, string>

const initialForm: FormState = {}

const FILE_DATE_FORMAT_CHOICES = [
  'dd-mm-yyyy',
  'dd/mm/yyyy',
  'mm-dd-yyyy',
  'mm/dd/yyyy',
  'yyyy-mm-dd',
  'yyyy/mm/dd',
] as const
const EMPTY_SELECT_VALUE = '__empty__'

type DynamicSchemaField = {
  type?: string
  key?: string
  label?: string
  placeholder?: string
  toolTip?: string
  validators?: string[]
  options?: string[]
  show?: boolean
  defaultValue?: string | number
  order?: number
}

type DynamicSchemaDefinition = {
  source?: DynamicSchemaField[]
  datawarehouse?: DynamicSchemaField[]
}

type ConnectorDisplayMeta = {
  name: string
  instance: string
  type: string
  image: string
}

// ─── DQ Check types ───────────────────────────────────────────────────────────

type DqModalType = 'Range Check' | 'Isin Set' | 'Length Check' | 'Format Check'

const DQ_MODAL_CHECKS: DqModalType[] = ['Range Check', 'Isin Set', 'Length Check', 'Format Check'];
const DQ_MODAL_TOOLTIP_MESSAGES: Record<DqModalType, string> = {
  'Range Check': "Range check can be applied on Numerical data's only",
  'Isin Set': 'Isin Set can be applied only on categorical columns',
  'Length Check': 'Length check validates the length of data to ensure it meets specified constraints.',
  'Format Check': 'Format check identifies and validates specific patterns or formats within the data.',
};

type DqModalValues = {
  rangeMin: string
  rangeMax: string
  isinValue: string
  lengthValue: string
  formatValue: string
}

const initialDqModalValues: DqModalValues = {
  rangeMin: '',
  rangeMax: '',
  isinValue: '',
  lengthValue: '',
  formatValue: '',
}

type ColumnFieldConfig = {
  selected: boolean
  fieldDataType: string
  isBusinessPrimaryKey: boolean
  dqChecks: string[]
  dqModalValues: DqModalValues
}

function getFileExtensionWithDot(value: string): string {
  const v = value.trim()
  const dotIdx = v.lastIndexOf('.')
  if (dotIdx <= 0 || dotIdx === v.length - 1) return ''
  return v.slice(dotIdx).toLowerCase()
}

function stripFileExtension(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  const slashIdx = trimmed.lastIndexOf('/')
  const segmentStart = slashIdx >= 0 ? slashIdx + 1 : 0
  const fileSegment = trimmed.slice(segmentStart)
  const dotIdx = fileSegment.lastIndexOf('.')
  if (dotIdx <= 0) return trimmed
  const prefix = trimmed.slice(0, segmentStart)
  return `${prefix}${fileSegment.slice(0, dotIdx)}`
}

function removeFileNameFromPath(pathValue: string, fileNameValue: string): string {
  const path = pathValue.trim()
  if (!path) return ''
  const fileName = fileNameValue.trim()
  if (!fileName) {
    const slash = path.lastIndexOf('/')
    return slash > 0 ? path.slice(0, slash) : path
  }

  // Try exact filename first.
  if (path.endsWith(`/${fileName}`)) {
    return path.slice(0, path.length - fileName.length - 1)
  }

  // Fallback: compare normalized names without extension.
  const baseName = stripFileExtension(fileName)
  const pathLastSegment = path.slice(path.lastIndexOf('/') + 1)
  if (stripFileExtension(pathLastSegment) === baseName) {
    const slash = path.lastIndexOf('/')
    return slash > 0 ? path.slice(0, slash) : path
  }

  return path
}

function toPayloadString(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return ''
  }
}

function firstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (value == null) continue
    const normalized = String(value).trim()
    if (normalized) return normalized
  }
  return ''
}

/** Whether connector id/label is S3 (handles `aws_s3` where `normalizedType` is only `aws`). */
function connectorIdLooksLikeS3(value: string | null | undefined): boolean {
  const s = String(value ?? '')
    .trim()
    .toLowerCase()
  if (!s) return false
  if (s === 's3' || s === 'aws_s3' || s === 'aws-s3') return true
  if (s.includes('s3')) return true
  if (s.split(/[_\s-]+/).some(p => p === 's3')) return true
  return false
}

function buildDqCheckPayload(cfg: ColumnFieldConfig): string {

  return cfg.dqChecks
    .map(check => {
      switch (check) {
        case 'Null Check':
          return 'none'
        case 'Redundant Check':
          return 'duplicate'
        case 'Blank Check':
          return 'blank'
        case 'No Special Characters Check':
          return 'no_special_chars'
        case 'Range Check':
          return `range?${cfg.dqModalValues.rangeMin}/${cfg.dqModalValues.rangeMax}`
        case 'Isin Set':
          return `isin?${cfg.dqModalValues.isinValue}`
        case 'Length Check':
          return `length?${cfg.dqModalValues.lengthValue}`
        case 'Format Check':
          return `pattern?${cfg.dqModalValues.formatValue}`
        default:
          return ''
      }
    })
    .filter(Boolean)
    .join(', ')

}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Host, port, and optional schema from `host:port?schema` (schema is the query segment after `?`). */
function parseRegisteredEndpoint(endpoint: string): { host: string; port: string; schema: string } {
  const trimmed = endpoint.trim()
  const q = trimmed.indexOf('?')
  const hostPort = (q >= 0 ? trimmed.slice(0, q) : trimmed).trim()
  const schema = q >= 0 ? trimmed.slice(q + 1).trim() : ''
  const colonIdx = hostPort.lastIndexOf(':')
  if (colonIdx <= 0) return { host: hostPort, port: '', schema }
  return {
    host: hostPort.slice(0, colonIdx),
    port: hostPort.slice(colonIdx + 1),
    schema,
  }
}

/** Build POST-shaped field rows from a preloaded registered table (no POST /source-files). */
function buildRegisteredFieldsFromPreloadedTable(
  listItem: AdpSourceFileListItem,
  selectedColumns: AdpConnectionColumnItem[],
  columnConfigs: Record<string, ColumnFieldConfig>,
  sourceDataSourceId: number
): AdpSourceFileRegisteredField[] {
  return selectedColumns.map((col, i) => {
    const cfg = columnConfigs[col.column_name]!
    const listField = listItem.fields.find(f => f.field_name === col.column_name)
    const persistedFieldId = listField?.source_file_field_id
    const sourceFileFieldId =
      typeof persistedFieldId === 'number' && Number.isFinite(persistedFieldId)
        ? persistedFieldId
        : listItem.source_file_id * 10_000 + i + 1
    return {
      source_file_field_id: sourceFileFieldId,
      roo_id: Number(listItem.roo_id ?? 0),
      source_data_source_id: Number(listItem.source_data_source_id ?? sourceDataSourceId),
      source_file_id: listItem.source_file_id,
      field_name: col.column_name,
      field_data_type: cfg.fieldDataType ?? col.data_type,
      field_structure: '',
      field_size: 0,
      precision: 0,
      is_business_primary_key: cfg.isBusinessPrimaryKey,
      dq_check: buildDqCheckPayload(cfg),
    }
  })
}

const DQ_CHECK_OPTIONS = [
  'Null Check',
  'Redundant Check',
  'Range Check',
  'Isin Set',
  'Length Check',
  'Format Check',
  'Blank Check',
  'No Special Characters Check',
] as const

const DATA_TYPE_FALLBACKS = [
  'string',
  'int',
  'float',
  'boolean',
  'timestamp',
  'date',
  'bigint',
] as const

function dataTypeSelectOptions(apiType: string): string[] {
  const t = apiType.trim()

  const lower = t.toLowerCase()

  const fromList = DATA_TYPE_FALLBACKS.filter(x => x !== lower)

  return t ? [t, ...fromList] : [...DATA_TYPE_FALLBACKS]
}

function formatDqMetric(value: number | null | undefined): string {
  if (value == null || (typeof value === 'number' && Number.isNaN(value))) return '—'

  return String(value)
}

function profilingDistinctDisplay(stats: AdpDqStatRecord | undefined): string {
  if (!stats) return '—'

  if (stats.distinct_count != null) return formatDqMetric(stats.distinct_count)

  if (stats.distinct_percent != null) return `${stats.distinct_percent}%`

  return '—'
}

function profilingNonDistinctDisplay(stats: AdpDqStatRecord | undefined): string {
  if (!stats) return '—'

  if (stats.duplicate != null) return formatDqMetric(stats.duplicate)

  if (
    stats.total_count != null &&
    stats.distinct_count != null &&
    !Number.isNaN(stats.total_count - stats.distinct_count)
  ) {
    return formatDqMetric(stats.total_count - stats.distinct_count)
  }

  return '—'
}

function profilingNullDisplay(stats: AdpDqStatRecord | undefined): string {
  if (!stats) return '—'

  if (stats.null_value != null) return formatDqMetric(stats.null_value)

  if (stats.null_percent != null) return `${stats.null_percent}%`

  return '—'
}

function formatProfilingDetailValue(value: string | number | null | undefined): string {
  if (value == null) return '—'

  if (typeof value === 'number' && Number.isNaN(value)) return '—'

  return String(value)
}

function coerceFiniteNumber(value: unknown, fallback = 0): number {
  if (value == null) return fallback

  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function toDisplaySourceTypeLabel(label: string): string {
  const trimmed = label.trim()
  if (!trimmed) return ''

  const assetKeyPattern = /^([a-z0-9]+(?:_[a-z0-9]+)*)_source_\d+$/i
  const assetKeyMatch = assetKeyPattern.exec(trimmed)
  const candidate = assetKeyMatch?.[1] ?? trimmed

  return candidate
    .split(/[_-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

function toDisplayFieldKeyLabel(key: string): string {
  const trimmed = key.trim()
  if (!trimmed) return ''

  return trimmed
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ConfigureDataSourceSheet({
  onClose,
  onBackToChoose,
  configuringNodeId = null,
  sourceTitle = 'Source 1',
  sourceTypeLabel = 'Postgres_source_123',
  connectorImage = '/adp-databases/postgresql.svg',
  registeredSource = null,
  allowedInstance = null,
}: Readonly<ConfigureDataSourceSheetProps>) {
  const [form, setForm] = useState<FormState>(initialForm)
  const [dynamicConfigFields, setDynamicConfigFields] = useState<DynamicSchemaField[]>([])
  const [isDynamicConfigLoading, setIsDynamicConfigLoading] = useState(false)
  const [connectorMeta, setConnectorMeta] = useState<ConnectorDisplayMeta | null>(null)
  const [prerequisiteData, setPrerequisiteData] = useState<unknown>(null)
  const [prereqModalOpen, setPrereqModalOpen] = useState(false);
  const [explorerOpen, setExplorerOpen] = useState(false)

  const [profilingOpen, setProfilingOpen] = useState(false)

  const [configurationOpen, setConfigurationOpen] = useState(true)

  const [isTesting, setIsTesting] = useState(false)

  const [testError, setTestError] = useState<string | null>(null)

  const [testResponseFields, setTestResponseFields] = useState<AdpConnectionTestOptionField[]>([])

  const [responseSelections, setResponseSelections] = useState<Record<string, string>>({})

  const [discoveredTables, setDiscoveredTables] = useState<string[]>([])

  const [isSchemaLoading, setIsSchemaLoading] = useState(false)

  const [selectedTableName, setSelectedTableName] = useState('')

  const [tableColumns, setTableColumns] = useState<AdpConnectionColumnItem[]>([])

  const [isColumnsLoading, setIsColumnsLoading] = useState(false)

  const [profilingChoice, setProfilingChoice] = useState<'yes' | 'no' | null>(null)

  const [profilingRequestNonce, setProfilingRequestNonce] = useState(0)

  const [columnConfigs, setColumnConfigs] = useState<Record<string, ColumnFieldConfig>>({})

  const [isSavingSourceFile, setIsSavingSourceFile] = useState(false)

  const [saveSourceFileError, setSaveSourceFileError] = useState<string | null>(null)

  const [saveSourceFileSuccess, setSaveSourceFileSuccess] = useState(false)

  const [lastRegisteredFields, setLastRegisteredFields] = useState<
    AdpSourceFileRegisteredField[] | null
  >(null)

  const [profilingPipelineLoading, setProfilingPipelineLoading] = useState(false)

  const [profilingPipelineError, setProfilingPipelineError] = useState<string | null>(null)

  const [profilingPipelineSuccess, setProfilingPipelineSuccess] = useState(false)

  const [profilingWsStatus, setProfilingWsStatus] = useState<string | null>(null)

  const [profilingDqStatRows, setProfilingDqStatRows] = useState<AdpDqStatRecord[]>([])

  const [expandedProfilingFieldId, setExpandedProfilingFieldId] = useState<number | null>(null)

  const profilingRunIdRef = useRef(0)

  const [registeredSourceFilesByTable, setRegisteredSourceFilesByTable] = useState<Record<
    string,
    AdpSourceFileListItem
  > | null>(null)

  const [registeredSourceFilesLoadError, setRegisteredSourceFilesLoadError] = useState<
    string | null
  >(null)

  const [isLoadingRegisteredSourceFiles, setIsLoadingRegisteredSourceFiles] = useState(false)
  const [registeredAddNewRequested, setRegisteredAddNewRequested] = useState(false)
  const [hideExplorerForRegisteredSelection, setHideExplorerForRegisteredSelection] = useState(false)

  /** When true, last save skipped POST /source-files; profiling rows may align DQ stats by index. */
  const [lastSaveSkippedPost, setLastSaveSkippedPost] = useState(false)

  // ─── DQ modal state ─────────────────────────────────────────────────────────

  const [dqModal, setDqModal] = useState<{
    columnName: string

    checkType: DqModalType
  } | null>(null)

  const [dqModalDraft, setDqModalDraft] = useState<DqModalValues>(initialDqModalValues)

  // ─── Validation errors for dynamic form fields ──────────────────────────────
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  // ─── DQ dropdown state ──────────────────────────────────────────────────────

  const [openDqDropdown, setOpenDqDropdown] = useState<string | null>(null)
  const [dqDropdownDirectionByColumn, setDqDropdownDirectionByColumn] = useState<Record<string, 'up' | 'down'>>({})

  /** When `file_delimiter` is `Custom` (revamped: dialog); real char stored here until Save. */
  const [excelCustomDelimiter, setExcelCustomDelimiter] = useState('')

  const dropdownRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  const allColumnsSelected =
    tableColumns.length > 0 &&
    tableColumns.every(column => (columnConfigs[column.column_name]?.selected ?? true))
  /** Akashic-revamped-ui stores the picked workbook as `excel_file` (data URL) for other steps; we mirror for parity. */
  const excelPickedFileRef = useRef<File | null>(null)

  const persistSourceDraftNow = useCallback(
    (overrides?: Partial<{
      form: FormState
      dynamicConfigFields: DynamicSchemaField[]
      configurationOpen: boolean
      explorerOpen: boolean
      profilingOpen: boolean
      hideExplorerForRegisteredSelection: boolean
      registeredAddNewRequested: boolean
      testResponseFields: AdpConnectionTestOptionField[]
      responseSelections: Record<string, string>
      discoveredTables: string[]
      selectedTableName: string
      tableColumns: AdpConnectionColumnItem[]
      columnConfigs: Record<string, ColumnFieldConfig>
    }>) => {
      if (!configuringNodeId) return
      upsertSourceConfigDraft(configuringNodeId, {
        form: (overrides?.form ?? form) as Record<string, string>,
        dynamicConfigFields: (overrides?.dynamicConfigFields ?? dynamicConfigFields) as Array<
          Record<string, unknown>
        >,
        configurationOpen: overrides?.configurationOpen ?? configurationOpen,
        explorerOpen: overrides?.explorerOpen ?? explorerOpen,
        profilingOpen: overrides?.profilingOpen ?? profilingOpen,
        hideExplorerForRegisteredSelection:
          overrides?.hideExplorerForRegisteredSelection ?? hideExplorerForRegisteredSelection,
        registeredAddNewRequested:
          overrides?.registeredAddNewRequested ?? registeredAddNewRequested,
        testResponseFields: (overrides?.testResponseFields ?? testResponseFields) as Array<
          Record<string, unknown>
        >,
        responseSelections: overrides?.responseSelections ?? responseSelections,
        discoveredTables: overrides?.discoveredTables ?? discoveredTables,
        selectedTableName: overrides?.selectedTableName ?? selectedTableName,
        tableColumns: (overrides?.tableColumns ?? tableColumns) as Array<Record<string, unknown>>,
        columnConfigs: (overrides?.columnConfigs ?? columnConfigs) as Record<string, unknown>,
      })
    },
    [
      columnConfigs,
      configurationOpen,
      configuringNodeId,
      discoveredTables,
      dynamicConfigFields,
      explorerOpen,
      form,
      hideExplorerForRegisteredSelection,
      profilingOpen,
      registeredAddNewRequested,
      responseSelections,
      selectedTableName,
      tableColumns,
      testResponseFields,
    ]
  )
  const excelIngestionDbRef = useRef<Record<string, unknown> | null>(null)
  const excelSheetNamesRef = useRef<string[]>([])
  const excelSheetPathsRef = useRef<string[]>([])

  const updateDqDropdownDirection = useCallback((columnName: string): void => {
    const dropdownAnchor = dropdownRefs.current.get(columnName)
    if (!dropdownAnchor) return

    const anchorRect = dropdownAnchor.getBoundingClientRect()
    const estimatedDropdownHeight = 240
    const viewportBottomPadding = 12
    const spaceBelow = window.innerHeight - anchorRect.bottom - viewportBottomPadding

    setDqDropdownDirectionByColumn(prev => ({
      ...prev,
      [columnName]: spaceBelow < estimatedDropdownHeight ? 'up' : 'down',
    }))
  }, [])

  const sourceNameAlreadyExists =
    testError?.toLowerCase().includes('source name already exists') ?? false

  const lastSourceNameExistsToastErrorRef = useRef<string | null>(null)

  const dqStatsWaitRef = useRef<{
    resolve: () => void
    reject: (err: any) => void
    sourceFileId: number
    sessionId: string
  } | null>(null)

  const resetProfilingAfterFailure = useCallback(
    (errorMessage: string) => {
      toast.error(errorMessage)
      setProfilingOpen(false)
      setProfilingChoice('no')
      setProfilingPipelineError(null)
      setProfilingPipelineSuccess(false)
      setProfilingDqStatRows([])
      setProfilingWsStatus(null)
      setProfilingPipelineLoading(false)
      dqStatsWaitRef.current = null
      profilingRunIdRef.current += 1
    },
    [toast]
  )

  const { startListening } = useJobMonitoringWebSocket({
    onMessage: msg => {
      if (msg.messageType !== 'dq_stats') return

      const rawStatus = String(msg.status ?? '').trim()
      const status = rawStatus.toLowerCase()
      const explicitSourceFileIdRaw =
        (msg as { source_file_id?: number | string; sourceFileId?: number | string })
          .source_file_id ?? (msg as { sourceFileId?: number | string }).sourceFileId
      const explicitSourceFileId = Number(explicitSourceFileIdRaw)
      const explicitSessionIdRaw =
        (msg as { session_id?: string | number; sessionId?: string | number }).session_id ??
        (msg as { sessionId?: string | number }).sessionId
      const explicitSessionId = String(explicitSessionIdRaw ?? '').trim()

      console.log('[DQ Stats WS] Message received', {
        status: rawStatus || '(empty)',
        id: msg.id ?? null,
        explicitSourceFileId: Number.isFinite(explicitSourceFileId) ? explicitSourceFileId : null,
        explicitSessionId: explicitSessionId || null,
        sender: msg.sender ?? null,
        isBroadcast: msg.isBroadcast ?? null,
        error: msg.error ?? null,
      })

      const wait = dqStatsWaitRef.current
      if (!wait) {
        console.log('[DQ Stats WS] Ignored: no active profiling wait state')
        return
      }

      // If message includes an explicit source_file_id and it does not match current run, ignore it.
      if (Number.isFinite(explicitSourceFileId) && explicitSourceFileId !== wait.sourceFileId) {
        console.log('[DQ Stats WS] Ignored: explicit source_file_id does not match current run', {
          explicitSourceFileId,
          waitingSourceFileId: wait.sourceFileId,
        })
        return
      }
      if (explicitSessionId && explicitSessionId !== wait.sessionId) {
        console.log('[DQ Stats WS] Ignored: explicit session_id does not match current run', {
          explicitSessionId,
          waitingSessionId: wait.sessionId,
        })
        return
      }

      setProfilingWsStatus(rawStatus || 'running')

      if (status === 'completed' || status === 'finished' || status === 'success') {
        console.log('[DQ Stats WS] Completed; fetching dq_stats records next', {
          sourceFileId: wait.sourceFileId,
          sessionId: wait.sessionId,
        })
        wait.resolve()
        dqStatsWaitRef.current = null
      } else if (status === 'failed' || status === 'error') {
        const errMsg = (msg.error as string) || 'Data profiling job failed.'
        console.log('[DQ Stats WS] Failed', {
          sourceFileId: wait.sourceFileId,
          sessionId: wait.sessionId,
          error: errMsg,
        })
        dqStatsWaitRef.current = null
        resetProfilingAfterFailure(errMsg)
        wait.reject(new Error(errMsg))
      }
    },
  })

  useEffect(() => {
    startListening()
  }, [startListening])

  useEffect(() => {
    if (!sourceNameAlreadyExists || !testError) {
      lastSourceNameExistsToastErrorRef.current = null
      return
    }

    // Avoid spamming the toast when the same error is re-rendered.
    if (lastSourceNameExistsToastErrorRef.current === testError) return

    lastSourceNameExistsToastErrorRef.current = testError
    toast.error('Source name already exists.')
  }, [sourceNameAlreadyExists, testError, toast])

  useEffect(() => {
    const savedDraft = configuringNodeId ? getSourceConfigDraft(configuringNodeId) : null
    if (savedDraft) return

    setRegisteredAddNewRequested(false)
    setHideExplorerForRegisteredSelection(Boolean(registeredSource))
    if (registeredSource) {
      setExplorerOpen(false)
      setProfilingOpen(false)
      setConfigurationOpen(true)
    }
  }, [configuringNodeId, registeredSource?.id])

  const primaryTestFieldKey = useMemo(() => testResponseFields[0]?.key ?? null, [testResponseFields])

  const schemaTestFields = useMemo(() => testResponseFields, [testResponseFields])

  const explorerExtraFields = useMemo(() => [] as AdpConnectionTestOptionField[], [])

  const dqStatsByFieldId = useMemo(() => {
    const m = new Map<number, AdpDqStatRecord>()

    for (const row of profilingDqStatRows) {
      m.set(row.source_file_field_id, row)
    }

    return m
  }, [profilingDqStatRows])

  const profilingDisplayRows = useMemo(() => {
    if (lastRegisteredFields?.length) {
      const dqSortedByFieldId =
        lastSaveSkippedPost && profilingDqStatRows.length === lastRegisteredFields.length
          ? [...profilingDqStatRows].sort((a, b) => a.source_file_field_id - b.source_file_field_id)
          : null
      return lastRegisteredFields.map((field, i) => {
        const byId = dqStatsByFieldId.get(field.source_file_field_id)
        const byIndex = dqSortedByFieldId?.[i]
        return {
          key: field.source_file_field_id,
          fieldName: field.field_name,
          fieldDataType: field.field_data_type,
          stats: byId ?? byIndex,
        }
      })
    }

    return [...profilingDqStatRows]

      .sort((a, b) => a.source_file_field_id - b.source_file_field_id)

      .map(row => ({
        key: row.source_file_field_id,

        fieldName:
          (row as AdpDqStatRecord & { field_name?: string }).field_name ||
          `Field #${row.source_file_field_id}`,

        fieldDataType:
          (row as AdpDqStatRecord & { field_data_type?: string }).field_data_type || '—',

        stats: row,
      }))
  }, [dqStatsByFieldId, lastRegisteredFields, lastSaveSkippedPost, profilingDqStatRows])

  const allExpanded = explorerOpen && profilingOpen

  // Close dropdown when clicking outside

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!openDqDropdown) return

      const target = event.target as Node

      const dropdownElement = dropdownRefs.current.get(openDqDropdown)

      if (dropdownElement && !dropdownElement.contains(target)) {
        setOpenDqDropdown(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openDqDropdown])

  useEffect(() => {

    if (!openDqDropdown) return

    const updateDirection = () => updateDqDropdownDirection(openDqDropdown)

    updateDirection()

    window.addEventListener('resize', updateDirection)

    window.addEventListener('scroll', updateDirection, true)

    return () => {

      window.removeEventListener('resize', updateDirection)

      window.removeEventListener('scroll', updateDirection, true)

    }

  }, [openDqDropdown])

  // ─── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const savedDraft = configuringNodeId ? getSourceConfigDraft(configuringNodeId) : null
    const savedAsset =
      configuringNodeId ? readAdpPipelineSession().assets[configuringNodeId] : null

    if (savedDraft) {
      setForm(savedDraft.form ?? initialForm)
      setDynamicConfigFields((savedDraft.dynamicConfigFields ?? []) as DynamicSchemaField[])
      setConfigurationOpen(savedDraft.configurationOpen ?? true)
      setExplorerOpen(savedDraft.explorerOpen ?? false)
      setProfilingOpen(savedDraft.profilingOpen ?? false)
      setHideExplorerForRegisteredSelection(savedDraft.hideExplorerForRegisteredSelection ?? false)
      setRegisteredAddNewRequested(savedDraft.registeredAddNewRequested ?? false)
      setTestResponseFields((savedDraft.testResponseFields ?? []) as AdpConnectionTestOptionField[])
      setResponseSelections(savedDraft.responseSelections ?? {})
      setDiscoveredTables(savedDraft.discoveredTables ?? [])
      setSelectedTableName(savedDraft.selectedTableName ?? '')
      setTableColumns((savedDraft.tableColumns ?? []) as AdpConnectionColumnItem[])
      setColumnConfigs((savedDraft.columnConfigs ?? {}) as Record<string, ColumnFieldConfig>)
      setTestError(null)
      setProfilingChoice(null)
      setSaveSourceFileError(null)
      setSaveSourceFileSuccess(false)
      setLastRegisteredFields(null)
      setProfilingPipelineError(null)
      setProfilingPipelineSuccess(false)
      setProfilingDqStatRows([])
      setExpandedProfilingFieldId(null)
      setLastSaveSkippedPost(false)
      if (!registeredSource) {
        setRegisteredSourceFilesByTable(null)
        setRegisteredSourceFilesLoadError(null)
        setIsLoadingRegisteredSourceFiles(false)
      }
      return
    }

    if (!registeredSource) {
      setForm(sourceTypeLabel?.trim() ? { source_name: sourceTypeLabel.trim() } : initialForm)
      setDynamicConfigFields([])
      setConfigurationOpen(true)
      setExplorerOpen(false)
      setProfilingOpen(false)
      setTestResponseFields([])
      setResponseSelections({})
      setDiscoveredTables([])
      setSelectedTableName('')
      setTableColumns([])
      setTestError(null)
      setProfilingChoice(null)
      setColumnConfigs({})
      setSaveSourceFileError(null)
      setSaveSourceFileSuccess(false)
      setLastRegisteredFields(null)
      setProfilingPipelineError(null)
      setProfilingPipelineSuccess(false)
      setProfilingDqStatRows([])
      setExpandedProfilingFieldId(null)
      setRegisteredSourceFilesByTable(null)
      setRegisteredSourceFilesLoadError(null)
      setIsLoadingRegisteredSourceFiles(false)
      setLastSaveSkippedPost(false)
      return
    }

    const {
      host,
      port,
      schema: endpointSchema,
    } = parseRegisteredEndpoint(registeredSource.endpoint)

    setForm({
      source_name: registeredSource.connectionName,
      host,
      port,
      database: registeredSource.databaseName,
      username: registeredSource.username,
      password: registeredSource.password,
      ...(endpointSchema ? { schema: endpointSchema } : {}),
    })

    setProfilingOpen(false)
    setTestResponseFields([])
    setResponseSelections(endpointSchema ? { schema: endpointSchema } : {})
    setDiscoveredTables([])
    setSelectedTableName('')
    setTableColumns([])
    setTestError(null)
    setProfilingChoice(null)
    setColumnConfigs(
      savedAsset?.columns?.length
        ? Object.fromEntries(
          savedAsset.columns.map(col => [
            col.name,
            {
              selected: col.selected ?? true,
              fieldDataType: col.dataType,
              isBusinessPrimaryKey: col.isBusinessPrimaryKey ?? false,
              dqChecks: col.dqChecks ?? [],
              dqModalValues: initialDqModalValues,
            },
          ])
        )
        : {}
    )
    setSaveSourceFileError(null)
    setSaveSourceFileSuccess(false)
    setLastRegisteredFields(null)
    setProfilingPipelineError(null)
    setProfilingPipelineSuccess(false)
    setProfilingDqStatRows([])
    setRegisteredSourceFilesLoadError(null)
    setRegisteredSourceFilesByTable(null)
    setLastSaveSkippedPost(false)

    let cancelled = false
    setIsLoadingRegisteredSourceFiles(true)
    setExplorerOpen(false)

    void (async () => {
      try {
        const rows = await fetchAdpSourceFilesList(Number(registeredSource.id))
        if (cancelled) return
        if (rows.length > 0) {
          const byTable = Object.fromEntries(rows.map(r => [r.table_name, r]))
          setRegisteredSourceFilesByTable(byTable)
          setDiscoveredTables(rows.map(r => r.table_name))
          setResponseSelections(prev => ({
            ...prev,
            ...(endpointSchema ? { schema: endpointSchema } : {}),
          }))
          const preferredTableName = savedAsset?.tableName?.trim() ?? ''
          const preferredRow = preferredTableName ? byTable[preferredTableName] : undefined
          const rowToSelect = preferredRow ?? rows[0]
          setSelectedTableName(rowToSelect.table_name)
          setTableColumns(
            rowToSelect.fields.map(f => ({ column_name: f.field_name, data_type: f.data_type }))
          )
          setConfigurationOpen(true)
          setExplorerOpen(false)
        } else {
          setRegisteredSourceFilesByTable(null)
          setConfigurationOpen(true)
          setExplorerOpen(false)
        }
      } catch (err) {
        if (!cancelled) {
          setRegisteredSourceFilesLoadError(
            err instanceof Error ? err.message : 'Failed to load registered source tables'
          )
          setRegisteredSourceFilesByTable(null)
          setConfigurationOpen(true)
          setExplorerOpen(false)
        }
      } finally {
        if (!cancelled) setIsLoadingRegisteredSourceFiles(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [configuringNodeId, registeredSource, sourceTypeLabel])

  useEffect(() => {
    let active = true
    const instance = (registeredSource?.dataSourceInstance || allowedInstance || '')
      .trim()
      .toLowerCase()
    if (!instance) {
      setDynamicConfigFields([])
      setConnectorMeta(null)
      setIsDynamicConfigLoading(false)
      return
    }

    const loadSchema = async () => {
      setIsDynamicConfigLoading(true)
      try {
        const dataSources = await fetchAdpDataSourcesFilter({
          column_name: ADP_DATA_SOURCES_FILTER_COLUMN_SOURCE,
          value: true,
          show_schema_definition: true,
          show_prerequisite: true,
        })
        if (!active) return
        const source = dataSources.find(
          ds => ds.data_source_instance?.trim().toLowerCase() === instance
        )
        const schemaDefinition = (source?.schema_definition ?? {}) as DynamicSchemaDefinition
        if (source) {
          setConnectorMeta({
            name: source.data_source_name,
            instance: source.data_source_instance,
            type: source.data_source_type,
            image: resolveAdpStaticAssetUrl(config.adpApiUrl, source.image_link),
          })
          setPrerequisiteData(source.prerequisite ?? null)
        } else {
          setConnectorMeta(null)
          setPrerequisiteData(null)
        }
        const fields = (schemaDefinition.source ?? [])
          .filter(field => field?.show !== false && !!field?.key)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        setDynamicConfigFields(fields)
        setForm(prev => {
          const next = { ...prev }
          fields.forEach(field => {
            const key = field.key ?? ''
            if (!key) return
            if (next[key]?.trim()) return
            if (field.defaultValue != null && String(field.defaultValue).trim()) {
              next[key] = String(field.defaultValue)
            }
          })
          return next
        })
      } catch {
        if (!active) return
        setDynamicConfigFields([])
        setConnectorMeta(null)
        setPrerequisiteData(null)
      } finally {
        if (active) {
          setIsDynamicConfigLoading(false)
        }
      }
    }

    void loadSchema()
    return () => {
      active = false
    }
  }, [registeredSource?.dataSourceInstance, allowedInstance])

  useEffect(() => {
    setColumnConfigs(prev => {
      const next: Record<string, ColumnFieldConfig> = {}

      for (const col of tableColumns) {
        const existing = prev[col.column_name]

        next[col.column_name] = existing ?? {
          selected: true,

          fieldDataType: col.data_type,

          isBusinessPrimaryKey: false,

          dqChecks: [],

          dqModalValues: initialDqModalValues,
        }
      }

      return next
    })
  }, [tableColumns])

  useEffect(() => {
    setSaveSourceFileSuccess(false)

    setSaveSourceFileError(null)

    setLastRegisteredFields(null)

    setProfilingPipelineError(null)

    setProfilingPipelineSuccess(false)

    setProfilingDqStatRows([])

    setExpandedProfilingFieldId(null)

    setLastSaveSkippedPost(false)
  }, [selectedTableName])

  useEffect(() => {
    if (!configuringNodeId) return

    upsertSourceConfigDraft(configuringNodeId, {
      form,
      dynamicConfigFields: dynamicConfigFields as Array<Record<string, unknown>>,
      configurationOpen,
      explorerOpen,
      profilingOpen,
      hideExplorerForRegisteredSelection,
      registeredAddNewRequested,
      testResponseFields: testResponseFields as Array<Record<string, unknown>>,
      responseSelections,
      discoveredTables,
      selectedTableName,
      tableColumns: tableColumns as Array<Record<string, unknown>>,
      columnConfigs: columnConfigs as Record<string, unknown>,
    })
  }, [
    columnConfigs,
    configuringNodeId,
    configurationOpen,
    discoveredTables,
    dynamicConfigFields,
    explorerOpen,
    form,
    hideExplorerForRegisteredSelection,
    profilingOpen,
    registeredAddNewRequested,
    responseSelections,
    selectedTableName,
    tableColumns,
    testResponseFields,
  ])

  useEffect(() => {
    if (profilingChoice !== 'yes' || profilingRequestNonce === 0) return

    const runId = ++profilingRunIdRef.current

    let active = true

    const run = async () => {
      setProfilingPipelineLoading(true)

      setProfilingPipelineError(null)

      setProfilingPipelineSuccess(false)

      setProfilingDqStatRows([])

      setProfilingWsStatus(null)

      setExpandedProfilingFieldId(null)

      try {
        let sourceFileId: number | undefined

        if (lastRegisteredFields?.length) {
          sourceFileId = Number(lastRegisteredFields[0].source_file_id)
        } else if (registeredSourceFilesByTable?.[selectedTableName]) {
          sourceFileId = registeredSourceFilesByTable[selectedTableName].source_file_id
        }

        if (sourceFileId == null || Number.isNaN(sourceFileId)) {
          throw new Error('Save the source file in Explorer before running data profiling.')
        }

        const sessionId = crypto.randomUUID()

        console.log('[DQ Stats Flow] Generated new profiling session', {
          sourceFileId: Number(sourceFileId),
          sessionId,
        })

        if (configuringNodeId) {
          upsertAssetProfilingSessionId(configuringNodeId, sessionId)
        }

        console.log('[DQ Stats Flow] Step 1/4 POST /dq-stats', {
          source_file_id: [String(sourceFileId)],
          session_id: [sessionId],
        })
        await postAdpDqStats({ source_file_id: [String(sourceFileId)], session_id: [sessionId] })

        if (!active || runId !== profilingRunIdRef.current) return

        console.log('[DQ Stats Flow] Step 2/4 POST /dq-stats/data-profiling', {
          source_file_id: Number(sourceFileId),
          session_id: sessionId,
        })
        await postAdpDqDataProfiling({
          session_id: sessionId,
          profiling_request_type: 'source',
          source_file_id: Number(sourceFileId),
        })

        if (!active || runId !== profilingRunIdRef.current) return

        // Wait for WebSocket "Completed" broadcast
        console.log('[DQ Stats Flow] Step 3/4 Waiting for dq_stats WebSocket completion', {
          sourceFileId: Number(sourceFileId),
          sessionId,
        })
        await new Promise<void>((resolve, reject) => {
          dqStatsWaitRef.current = {
            resolve,
            reject,
            sourceFileId: Number(sourceFileId),
            sessionId,
          }
          setProfilingWsStatus('running')
          // Safety timeout (5 mins) - profiling can take a while
          setTimeout(() => {
            if (dqStatsWaitRef.current?.sourceFileId === Number(sourceFileId)) {
              setProfilingWsStatus('timed_out')
              dqStatsWaitRef.current.reject(
                new Error('Data profiling timed out waiting for server completion.')
              )
              dqStatsWaitRef.current = null
            }
          }, 300000)
        })

        if (!active || runId !== profilingRunIdRef.current) return

        console.log('[DQ Stats Flow] Step 4/4 GET /records/dq_stats', {
          sourceFileId: Number(sourceFileId),
          sessionId,
        })
        const dqRows = await fetchAdpDqStatsBySourceFileId(Number(sourceFileId), sessionId)

        if (!active || runId !== profilingRunIdRef.current) return

        setProfilingDqStatRows(dqRows)

        setProfilingPipelineSuccess(true)
      } catch (err) {
        if (runId !== profilingRunIdRef.current) return

        const message = err instanceof Error ? err.message : 'Data profiling failed'

        resetProfilingAfterFailure(message)
      } finally {
        if (active && runId === profilingRunIdRef.current) setProfilingPipelineLoading(false)
      }
    }

    void run()

    return () => {
      active = false
    }
  }, [
    profilingChoice,
    profilingRequestNonce,
    lastRegisteredFields,
    registeredSourceFilesByTable,
    selectedTableName,
  ])

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleExpandAll = useCallback(() => {
    if (allExpanded) {
      setExplorerOpen(false)
      setProfilingOpen(false)
    } else {
      setExplorerOpen(true)
      setProfilingOpen(true)
    }
  }, [allExpanded])

  const normalizedType = useMemo(() => {
    const fromRegistered = registeredSource?.dataSourceInstance?.trim()

    if (fromRegistered) return fromRegistered.toLowerCase().split(/[_\s-]/)[0] || 'postgres'

    if (allowedInstance?.trim()) return allowedInstance.trim().toLowerCase()

    return (
      sourceTypeLabel
        .trim()
        .toLowerCase()
        .split(/[_\s-]/)[0] || 'postgres'
    )
  }, [sourceTypeLabel, registeredSource, allowedInstance])

  const displayTitle = registeredSource?.connectionName ?? connectorMeta?.name ?? sourceTitle

  const fallbackTypeLabel = useMemo(
    () => toDisplaySourceTypeLabel(sourceTypeLabel ?? ''),
    [sourceTypeLabel]
  )

  const displayTypeLabel = registeredSource
    ? toDisplaySourceTypeLabel(registeredSource.dataSourceInstance)
    : connectorMeta
      ? toDisplaySourceTypeLabel(connectorMeta.instance)
      : fallbackTypeLabel

  const displayImage =
    registeredSource?.image?.trim() ||
    connectorMeta?.image?.trim() ||
    connectorImage?.trim() ||
    '/adp-databases/postgresql.svg'

  const isExcelSource = useMemo(() => {
    const normalized = normalizedType?.trim().toLowerCase()
    const registered = registeredSource?.dataSourceInstance?.trim().toLowerCase() ?? ''
    const label = sourceTypeLabel?.trim().toLowerCase() ?? ''
    return (
      ['excel', 'xls', 'xlsx'].includes(normalized) ||
      ['excel', 'xls', 'xlsx'].includes(registered) ||
      label.includes('excel') ||
      registered.includes('excel')
    )
  }, [normalizedType, registeredSource?.dataSourceInstance, sourceTypeLabel])

  /** File System / Gen2 FS in the palette — same file + presigned + delimiter pattern as Excel for local files. */
  const isFilesystemLikeSource = useMemo(() => {
    const n = normalizedType?.trim().toLowerCase() ?? ''
    const reg = registeredSource?.dataSourceInstance?.trim().toLowerCase() ?? ''
    const label = sourceTypeLabel?.trim().toLowerCase() ?? ''
    if (n === 'filesystem' || n === 'gen2filesystem') return true
    if (reg === 'filesystem' || reg === 'gen2filesystem') return true
    if (/\bfilesystem\b/i.test(reg) || /\bgen2filesystem\b/i.test(reg)) return true
    if (label.includes('file system') || label.includes('filesystem')) return true
    return false
  }, [normalizedType, registeredSource?.dataSourceInstance, sourceTypeLabel])

  const isS3LikeSource = useMemo(() => {
    const n = normalizedType?.trim().toLowerCase() ?? ''
    const reg = registeredSource?.dataSourceInstance?.trim().toLowerCase() ?? ''
    const label = sourceTypeLabel?.trim().toLowerCase() ?? ''
    const allowed = allowedInstance?.trim().toLowerCase() ?? ''
    const inst = connectorMeta?.instance?.trim().toLowerCase() ?? ''
    // `normalizedType` uses only the first token of `data_source_instance`, so e.g. `aws_s3` -> `aws`.
    // Substring `\bs3\b` also misses `aws_s3` (underscore is a word char). So check tokens + substring `s3`.
    if (n === 's3' || n === 'aws_s3') return true
    if (n === 'aws' && [reg, label, inst, allowed].some(s => s.includes('s3'))) return true
    for (const s of [reg, label, inst, allowed]) {
      if (!s) continue
      if (s === 's3' || s === 'aws_s3' || s.split(/[_\s-]+/).includes('s3')) return true
      if (s.includes('aws') && s.includes('s3')) return true
    }
    for (const s of [reg, label, inst, allowed, n]) {
      if (s && s.replace(/[-_]+/g, ' ').split(/\s+/).includes('s3')) return true
    }
    if (isFilesystemLikeSource && (label.includes('s3') || inst.includes('s3'))) return true
    return false
  }, [
    allowedInstance,
    connectorMeta?.instance,
    isFilesystemLikeSource,
    normalizedType,
    registeredSource?.dataSourceInstance,
    sourceTypeLabel,
  ])

  /** Excel or File System: presigned URL upload, Save (not Test), delimiter for `.csv`/`.txt`/`.tsv`, Explorer columns. */
  const isPresignedFileIngestionSource = useMemo(
    () => isExcelSource || isFilesystemLikeSource,
    [isExcelSource, isFilesystemLikeSource]
  )

  /** Show Delimiter row for Excel + File System when the schema does not define `file_delimiter`. */
  const showInjectedFileDelimiter = useMemo(
    () =>
      (isPresignedFileIngestionSource || isS3LikeSource) &&
      !dynamicConfigFields.some(f => f.key === 'file_delimiter'),
    [isPresignedFileIngestionSource, isS3LikeSource, dynamicConfigFields]
  )

  const showInjectedFileDateFormat = useMemo(
    () =>
      (isPresignedFileIngestionSource || isS3LikeSource) &&
      !dynamicConfigFields.some(f => f.key === 'file_date_format' || f.key === 'date_format'),
    [isPresignedFileIngestionSource, isS3LikeSource, dynamicConfigFields]
  )

  const hasPreloadedRegisteredTables =
    registeredSourceFilesByTable !== null && Object.keys(registeredSourceFilesByTable).length > 0

  const isRegisteredSourceSelected = Boolean(registeredSource)
  const notRegisteredTablesQuery = useQuery({
    queryKey: [
      'adp',
      'source-data-sources',
      'not-registered-tables',
      registeredSource?.id ?? '',
      registeredSource?.dataSourceInstance ?? '',
    ],
    enabled:
      isRegisteredSourceSelected &&
      registeredAddNewRequested &&
      Boolean(registeredSource?.id?.trim()) &&
      Boolean(registeredSource?.dataSourceInstance?.trim()),
    queryFn: () =>
      getNotRegisteredTables(
        String(registeredSource?.id ?? ''),
        String(registeredSource?.dataSourceInstance ?? '')
      ),
    retry: false,
  })

  const connectionFieldsLocked =
    isRegisteredSourceSelected ||
    testResponseFields.length > 0 ||
    isLoadingRegisteredSourceFiles ||
    hasPreloadedRegisteredTables

  const delimiterControlEnabled = useMemo(
    () => {
      if (isS3LikeSource) {
        return !connectionFieldsLocked
      }
      const activeFileName = (form.file_name ?? selectedTableName ?? '').trim()
      return !connectionFieldsLocked && isDelimitedTextFileName(activeFileName)
    },
    [connectionFieldsLocked, form.file_name, isS3LikeSource, selectedTableName]
  )

  const showDynamicConfigSkeleton = dynamicConfigFields.length === 0 && isDynamicConfigLoading

  const connectionPayload = useMemo(() => {
    const payload: Record<string, unknown> = { type: normalizedType }
    Object.entries(form).forEach(([key, value]) => {
      const v = value?.trim()
      if (!v) return
      // Keep file parsing fields out of connection/test payloads.
      // They are sent later in /source-files registration payload only.
      if (key === 'file_delimiter' || key === 'date_format' || key === 'file_date_format') {
        return
      }
      if (key === 'port') {
        const port = Number(v)
        payload[key] = Number.isFinite(port) ? port : v
      } else {
        payload[key] = v
      }
    })
    if (!payload.source_name && typeof payload.sourceName === 'string') {
      payload.source_name = payload.sourceName
    }
    return payload
  }, [form, normalizedType])

  const loadPresignedSourceColumns = useCallback(
    async (tableName: string): Promise<AdpConnectionColumnItem[]> => {
      const names = excelSheetNamesRef.current
      const paths = excelSheetPathsRef.current
      const idx = names.indexOf(tableName)
      const base = excelIngestionDbRef.current ?? {}
      const tablePath =
        idx >= 0 && paths[idx] != null ? String(paths[idx]) : String(base['table_path'] ?? '')
      const dateFromForm = (form['date_format'] ?? '').trim()
      const dateFormat =
        dateFromForm || (typeof base['date_format'] === 'string' ? base['date_format'] : '') || ''
      const sourceName =
        String(base['source_name'] ?? connectionPayload.source_name ?? form.source_name ?? '').trim()

      const payload: Record<string, unknown> = {
        ...base,
        type: String(base['type'] ?? normalizedType),
        table_name: tableName,
        table_path: tablePath,
        ...(dateFormat ? { date_format: dateFormat } : {}),
        ...(sourceName ? { source_name: sourceName } : {}),
      }

      return fetchAdpConnectionColumns([payload as AdpConnectionColumnsDatabasePayload])
    },
    [connectionPayload.source_name, form, normalizedType]
  )

  // ─── Form validation helper ─────────────────────────────────────────────────
  const validateForm = useCallback((): boolean => {
    const errors = validateFormFields(form, dynamicConfigFields)
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [form, dynamicConfigFields])

  const handleTest = useCallback(async () => {
    if (!validateForm()) return

    if (isPresignedFileIngestionSource) {
      const file = excelPickedFileRef.current
      if (!file) {
        toast.error('Choose a file for the file field, then click Save again.')
        return
      }
      setIsTesting(true)
      setTestError(null)
      try {
        const fileName = file.name
        const delRes = resolveFileDelimiterForApi(fileName, form, excelCustomDelimiter)
        if ('message' in delRes) {
          toast.error(delRes.message)
          setIsTesting(false)
          return
        }
        const { apiValue: fileDelimiterValue, includeInDatabases: includeFileDelimiter } = delRes
        const contentType = contentTypeForSourceFileName(fileName)
        const presignDatabases: PresignedUrlRequestBody['databases'] = [
          {
            type: normalizedType,
            ...(includeFileDelimiter && fileDelimiterValue ? { file_delimiter: fileDelimiterValue } : {}),
          },
        ]
        const presigned = await postFilesPresignedUrl({
          databases: presignDatabases,
          file_name: fileName,
          content_type: contentType,
        })
        await putFileToPresignedUrl(presigned.upload_url, file, contentType)
        const tablePath = (presigned.path ?? '').trim()
        if (!tablePath) {
          throw new Error('Presigned response did not return a path (path).')
        }

        const dbRow: Record<string, unknown> = { type: normalizedType, table_path: tablePath }
        for (const [k, v] of Object.entries(form)) {
          if (k === 'file_delimiter') {
            continue
          }
          const t = String(v).trim()
          if (t) {
            if (k === 'port') {
              const port = Number(t)
              dbRow[k] = Number.isFinite(port) ? port : t
            } else {
              dbRow[k] = t
            }
          }
        }
        if (includeFileDelimiter) {
          dbRow.file_delimiter = fileDelimiterValue
        }

        const showRows = await postShowTablesAndCreateV2({ databases: [dbRow] })
        const row = showRows[0]
        if (!row) {
          throw new Error('showTables did not return any data.')
        }

        const tableList = row.tables
        const pathStrings = Array.isArray(tableList) ? (tableList as string[]) : []
        let sheetNames: string[] = []
        let sheetPaths: string[] = []

        // Excel-specific response mapping:
        // {
        //   tables: { sheetPaths: [...], sheetNames: [...] }
        // }
        if (
          isExcelSource &&
          tableList &&
          typeof tableList === 'object' &&
          !Array.isArray(tableList)
        ) {
          const t = tableList as { sheetPaths?: unknown; sheetNames?: unknown }
          sheetPaths = Array.isArray(t.sheetPaths)
            ? t.sheetPaths.filter((v): v is string => typeof v === 'string')
            : []
          sheetNames = Array.isArray(t.sheetNames)
            ? t.sheetNames.filter((v): v is string => typeof v === 'string')
            : []
        } else {
          const parsed = parseExcelSheetsFromTablePaths(pathStrings)
          sheetNames = parsed.sheetNames
          sheetPaths = parsed.sheetPaths
        }

        const merged0: Record<string, unknown> =
          row.databases?.[0] && typeof row.databases[0] === 'object'
            ? { ...(row.databases[0] as Record<string, unknown>) }
            : { ...dbRow }

        if (row.date_format && typeof row.date_format === 'string' && !merged0.date_format) {
          merged0.date_format = row.date_format
        }

        excelIngestionDbRef.current = merged0
        excelSheetNamesRef.current = sheetNames
        excelSheetPathsRef.current = sheetPaths

        setLastSaveSkippedPost(false)
        setTestResponseFields([])
        setResponseSelections({})
        const discovered =
          sheetNames.length > 0 ? sheetNames : sheetPaths.length > 0 ? sheetPaths : pathStrings
        setDiscoveredTables(discovered)
        setSelectedTableName('')
        setTableColumns([])

        if (isExcelSource && discovered.length > 0) {
          const firstSheet = discovered[0] ?? ''
          if (firstSheet) {
            setSelectedTableName(firstSheet)
            setIsColumnsLoading(true)
            try {
              const cols = await loadPresignedSourceColumns(firstSheet)
              setTableColumns(cols)
            } catch (err) {
              setTableColumns([])
              setTestError(err instanceof Error ? err.message : 'Failed to auto-load sheet columns')
            } finally {
              setIsColumnsLoading(false)
            }
          }
        }

        setConfigurationOpen(false)
        setExplorerOpen(true)
        toast.success('File saved; select a sheet in Explorer.')
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'File source save failed'
        setTestError(errorMsg)
        toast.error(errorMsg)
      } finally {
        setIsTesting(false)
      }
      return
    }

    setIsTesting(true)
    setTestError(null)

    try {
      const fields = await testAdpConnection([
        connectionPayload as AdpConnectionTestDatabasePayload,
      ])

      setLastSaveSkippedPost(false)

      setTestResponseFields(fields)

      setResponseSelections(
        fields.reduce<Record<string, string>>((acc, field) => {
          acc[field.key] = field.defaultValue ?? ''

          return acc
        }, {})
      )

      setDiscoveredTables([])

      setSelectedTableName('')

      setTableColumns([])
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to test connection'
      setTestError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setIsTesting(false)
    }
  }, [connectionPayload, excelCustomDelimiter, form, isExcelSource, isPresignedFileIngestionSource, loadPresignedSourceColumns, normalizedType, validateForm])

  const handleSchemaSelection = useCallback(
    async (selectionKey: string, selectionValue: string) => {
      if (!selectionKey || !selectionValue) return
      if (!validateForm()) return

      setIsSchemaLoading(true)

      setTestError(null)

      setDiscoveredTables([])

      setSelectedTableName('')

      setTableColumns([])

      try {
        const selectionPayload = Object.entries(responseSelections).reduce<Record<string, string>>(
          (acc, [key, value]) => {
            const trimmed = value.trim()
            if (trimmed) acc[key] = trimmed
            return acc
          },
          {}
        )
        const tables = await createAdpSourceDataSource([
          {
            ...(connectionPayload as Record<string, unknown>),
            ...selectionPayload,
            [selectionKey]: selectionValue,
            source_name:
              String((connectionPayload.source_name as string | undefined) ?? '').trim() ||
              'test_source_new',
          } as AdpSourceDataSourceCreatePayload,
        ])

        setDiscoveredTables(tables)

        setConfigurationOpen(false)

        setExplorerOpen(true)
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to load source tables'
        setTestError(errorMsg)
        toast.error(errorMsg)
      } finally {
        setIsSchemaLoading(false)
      }
    },

    [connectionPayload, responseSelections, validateForm]
  )

  const selectedSchema =
    (primaryTestFieldKey ? responseSelections[primaryTestFieldKey] : '')?.trim() ?? ''

  const s3ShowFileParsingInExplorer = useMemo(
    () => selectedSchema.length > 0 || discoveredTables.length > 0,
    [selectedSchema, discoveredTables]
  )

  const handleTableSelection = useCallback(
    async (tableName: string) => {
      setSelectedTableName(tableName)
      if (!tableName) {
        setTableColumns([])
        persistSourceDraftNow({ selectedTableName: '', tableColumns: [] })
        return
      }

      const cached = registeredSourceFilesByTable?.[tableName]
      if (cached?.fields?.length) {
        const cachedColumns = cached.fields.map(f => ({
          column_name: f.field_name,
          data_type: f.data_type,
        }))
        setTableColumns(cachedColumns)
        persistSourceDraftNow({ selectedTableName: tableName, tableColumns: cachedColumns })
        return
      }

      if (isPresignedFileIngestionSource && excelIngestionDbRef.current) {
        setIsColumnsLoading(true)
        setTestError(null)
        try {
          const columns = await loadPresignedSourceColumns(tableName)
          setTableColumns(columns)
          persistSourceDraftNow({ selectedTableName: tableName, tableColumns: columns })
        } catch (err) {
          setTableColumns([])
          persistSourceDraftNow({ selectedTableName: tableName, tableColumns: [] })
          setTestError(err instanceof Error ? err.message : 'Failed to load columns for sheet')
        } finally {
          setIsColumnsLoading(false)
        }
        return
      }

      const selectionPayload = Object.entries(responseSelections).reduce<Record<string, string>>(
        (acc, [key, value]) => {
          const trimmed = value.trim()
          if (trimmed) acc[key] = trimmed
          return acc
        },
        {}
      )
      if (
        Object.keys(selectionPayload).length === 0 &&
        (!primaryTestFieldKey || !selectedSchema.trim())
      ) {
        return
      }

      setIsColumnsLoading(true)
      setTestError(null)
      try {
        const columns = await fetchAdpConnectionColumns([
          {
            ...(connectionPayload as Record<string, unknown>),
            ...selectionPayload,
            ...(primaryTestFieldKey && selectedSchema.trim()
              ? { [primaryTestFieldKey]: selectedSchema.trim() }
              : {}),
            table_name: tableName,
            source_name: String((connectionPayload.source_name as string | undefined) ?? ''),
          } as AdpConnectionColumnsDatabasePayload,
        ])
        setTableColumns(columns)
        persistSourceDraftNow({ selectedTableName: tableName, tableColumns: columns })
      } catch (err) {
        setTableColumns([])
        persistSourceDraftNow({ selectedTableName: tableName, tableColumns: [] })
        setTestError(err instanceof Error ? err.message : 'Failed to load table columns')
      } finally {
        setIsColumnsLoading(false)
      }
    },
    [
      connectionPayload,
      isPresignedFileIngestionSource,
      loadPresignedSourceColumns,
      primaryTestFieldKey,
      registeredSourceFilesByTable,
      responseSelections,
      selectedSchema,
      persistSourceDraftNow,
    ]
  )

  useEffect(() => {
    if (!registeredAddNewRequested) return
    if (notRegisteredTablesQuery.isLoading) return
    if (notRegisteredTablesQuery.isError) {
      setTestError(
        notRegisteredTablesQuery.error instanceof Error
          ? notRegisteredTablesQuery.error.message
          : 'Failed to load not registered tables'
      )
      return
    }
    // Preserve current table/columns if the user already selected a table.
    // Without this guard, a later query refresh can clear fetched columns.
    if (selectedTableName.trim() || tableColumns.length > 0) return
    const tables = notRegisteredTablesQuery.data?.data?.tables ?? []
    const schemaFromDb = notRegisteredTablesQuery.data?.data?.databases?.[0]?.schema?.trim() ?? ''
    const hasSameTables =
      discoveredTables.length === tables.length &&
      discoveredTables.every((table, index) => table === tables[index])
    const hasSameSchemaSelection = !schemaFromDb || (responseSelections.schema ?? '').trim() === schemaFromDb
    const alreadyHydrated =
      hasSameTables &&
      hasSameSchemaSelection &&
      !configurationOpen &&
      explorerOpen &&
      !profilingOpen &&
      selectedTableName.trim() === '' &&
      tableColumns.length === 0

    if (alreadyHydrated) return

    const nextResponseSelections = hasSameSchemaSelection
      ? responseSelections
      : {
        ...responseSelections,
        schema: schemaFromDb,
      }
    if (!hasSameSchemaSelection) {
      setResponseSelections(nextResponseSelections)
    }
    if (!hasSameTables) setDiscoveredTables(tables)
    if (configurationOpen) setConfigurationOpen(false)
    if (!explorerOpen) setExplorerOpen(true)
    if (profilingOpen) setProfilingOpen(false)
    persistSourceDraftNow({
      responseSelections: nextResponseSelections,
      discoveredTables: tables,
      selectedTableName: '',
      tableColumns: [],
      configurationOpen: false,
      explorerOpen: true,
      profilingOpen: false,
    })
  }, [
    registeredAddNewRequested,
    notRegisteredTablesQuery.isLoading,
    notRegisteredTablesQuery.isError,
    notRegisteredTablesQuery.error,
    notRegisteredTablesQuery.data,
    persistSourceDraftNow,
    configurationOpen,
    discoveredTables,
    explorerOpen,
    profilingOpen,
    responseSelections,
    selectedTableName,
    tableColumns.length,
  ])

  const handleRegisteredAddNewTable = useCallback(() => {
    setRegisteredAddNewRequested(true)
    setHideExplorerForRegisteredSelection(false)
    setSaveSourceFileError(null)
    setSaveSourceFileSuccess(false)
    setConfigurationOpen(false)
    setProfilingOpen(false)
    setExplorerOpen(true)
    persistSourceDraftNow({
      registeredAddNewRequested: true,
      hideExplorerForRegisteredSelection: false,
      configurationOpen: false,
      profilingOpen: false,
      explorerOpen: true,
    })
  }, [persistSourceDraftNow])

  const dataSourceNameForPayload = (
    registeredSource?.connectionName ??
    form.source_name ??
    form.destination_name ??
    ''
  ).trim()

  const dataSourceInstanceForPayload = (
    registeredSource?.dataSourceInstance?.trim() || normalizedType
  ).trim()

  const handleRegisteredExistingSave = useCallback(
    (selection: RegisteredExistingSourceSelection) => {
      const selectedFields = selection.fields.map(field => ({
        column_name: field.field_name,
        data_type: field.field_data_type,
      }))
      const nextColumnConfigs = Object.fromEntries(
        selection.fields.map(field => [
          field.field_name,
          {
            selected: true,
            fieldDataType: field.field_data_type,
            isBusinessPrimaryKey: field.is_business_primary_key,
            dqChecks: [],
            dqModalValues: initialDqModalValues,
          },
        ])
      ) as Record<string, ColumnFieldConfig>

      setSelectedTableName(selection.tableName)
      setTableColumns(selectedFields)
      setColumnConfigs(nextColumnConfigs)

      const normalizedSourceDataSourceId =
        selection.sourceDataSourceId || Number(registeredSource?.id ?? 0)
      const normalizedRooId = selection.rooId || (registeredSource?.rooId ?? 0)
      const registeredFields: AdpSourceFileRegisteredField[] = selection.fields.map(field => ({
        source_file_id: selection.sourceFileId,
        source_file_field_id: field.source_file_field_id,
        field_name: field.field_name,
        field_data_type: field.field_data_type,
        field_structure: '',
        field_size: 0,
        precision: 0,
        is_business_primary_key: field.is_business_primary_key,
        dq_check: '',
        source_data_source_id: normalizedSourceDataSourceId,
        roo_id: normalizedRooId,
      }))

      setLastRegisteredFields(registeredFields.length > 0 ? registeredFields : null)
      setLastSaveSkippedPost(true)
      setSaveSourceFileSuccess(true)
      setSaveSourceFileError(null)
      setRegisteredAddNewRequested(false)

      if (configuringNodeId) {
        const schemaName = (responseSelections.schema ?? '').trim() || undefined
        upsertAssetSession({
          nodeId: configuringNodeId,
          assetKind: 'source',
          dataSourceName: dataSourceNameForPayload,
          dataSourceInstance: dataSourceInstanceForPayload,
          rooId: normalizedRooId,
          sourceDataSourceId: normalizedSourceDataSourceId,
          sourceFileId: selection.sourceFileId,
          schemaName,
          tableName: selection.tableName,
          columns: selection.fields.map(field => ({
            name: field.field_name,
            dataType: field.field_data_type,
            selected: true,
            isBusinessPrimaryKey: field.is_business_primary_key,
          })),
        })
      }

      setHideExplorerForRegisteredSelection(true)
      setConfigurationOpen(false)
      setExplorerOpen(false)
      setProfilingOpen(true)
      persistSourceDraftNow({
        selectedTableName: selection.tableName,
        tableColumns: selectedFields,
        columnConfigs: nextColumnConfigs,
        hideExplorerForRegisteredSelection: true,
        registeredAddNewRequested: false,
        configurationOpen: false,
        explorerOpen: false,
        profilingOpen: true,
      })
    },
    [
      configuringNodeId,
      dataSourceInstanceForPayload,
      dataSourceNameForPayload,
      registeredSource?.id,
      registeredSource?.rooId,
      responseSelections.schema,
      persistSourceDraftNow,
    ]
  )

  const handleSaveSourceFile = useCallback(async () => {
    if (!selectedTableName || !dataSourceNameForPayload) {
      setSaveSourceFileError('Select a table and ensure the source has a name.')

      return
    }

    const selectedFields = tableColumns.filter(col => columnConfigs[col.column_name]?.selected)

    if (selectedFields.length === 0) {
      setSaveSourceFileError('Select at least one field to save.')
      return
    }

    setIsSavingSourceFile(true)
    setSaveSourceFileError(null)
    setSaveSourceFileSuccess(false)

    try {
      const fileName = selectedTableName
      const preloadedRow = registeredSourceFilesByTable?.[selectedTableName]
      const skipPost =
        Boolean(registeredSource) &&
        preloadedRow != null &&
        preloadedRow.table_name === selectedTableName

      const persistSession = (idPayload?: {
        rooId?: number
        sourceDataSourceId?: number
        sourceFileId?: number
      }) => {
        if (!configuringNodeId) return
        const schemaName = (responseSelections.schema ?? '').trim() || undefined
        upsertAssetSession({
          nodeId: configuringNodeId,
          assetKind: 'source',
          dataSourceName: dataSourceNameForPayload,
          dataSourceInstance: dataSourceInstanceForPayload,
          rooId: idPayload?.rooId,
          sourceDataSourceId: idPayload?.sourceDataSourceId,
          sourceFileId: idPayload?.sourceFileId,
          schemaName,
          tableName: selectedTableName,
          columns: selectedFields.map(col => {
            const cfg = columnConfigs[col.column_name]!
            return {
              name: col.column_name,
              dataType: cfg.fieldDataType ?? col.data_type,
              selected: true,
              isBusinessPrimaryKey: cfg.isBusinessPrimaryKey,
              dqChecks: cfg.dqChecks.length > 0 ? [...cfg.dqChecks] : undefined,
            }
          }),
        })
      }

      if (skipPost && preloadedRow) {
        const registered = buildRegisteredFieldsFromPreloadedTable(
          preloadedRow,
          selectedFields,
          columnConfigs,
          Number(registeredSource!.id)
        )
        setLastRegisteredFields(registered.length > 0 ? registered : null)
        setLastSaveSkippedPost(true)
        setSaveSourceFileSuccess(true)
        persistSession({
          rooId:
            typeof preloadedRow.roo_id === 'number' ? preloadedRow.roo_id : registeredSource?.rooId,
          sourceDataSourceId:
            typeof preloadedRow.source_data_source_id === 'number'
              ? preloadedRow.source_data_source_id
              : Number(registeredSource!.id),
          sourceFileId: preloadedRow.source_file_id,
        })
        setConfigurationOpen(false)
        setExplorerOpen(false)
        setProfilingOpen(true)
        persistSourceDraftNow({
          selectedTableName,
          tableColumns,
          columnConfigs,
          configurationOpen: false,
          explorerOpen: false,
          profilingOpen: true,
          hideExplorerForRegisteredSelection: true,
          registeredAddNewRequested: false,
        })
        return
      }

      setLastSaveSkippedPost(false)

      const sourceBase: Record<string, unknown> = isPresignedFileIngestionSource
        ? (excelIngestionDbRef.current ?? {})
        : (connectionPayload as Record<string, unknown>)
      const selectedSourceFileName = String(
        sourceBase.file_name ?? form.file_name ?? selectedTableName
      ).trim()
      const selectedTablePath = String(sourceBase.table_path ?? '').trim()
      const selectedFileDelimiter = firstNonEmptyString(
        sourceBase.file_delimiter,
        form.file_delimiter
      )
      const isS3ForSourceFiles =
        isS3LikeSource ||
        connectorIdLooksLikeS3(registeredSource?.dataSourceInstance) ||
        connectorIdLooksLikeS3(allowedInstance) ||
        connectorIdLooksLikeS3(connectorMeta?.instance) ||
        connectorIdLooksLikeS3(sourceTypeLabel) ||
        connectorIdLooksLikeS3(dataSourceInstanceForPayload)
      const sourceExtension =
        getFileExtensionWithDot(selectedSourceFileName) ||
        getFileExtensionWithDot(selectedTablePath) ||
        getFileExtensionWithDot(fileName)
      const fileLikeByPath = Boolean(selectedTablePath && sourceExtension)
      const isFileLikeSource = isPresignedFileIngestionSource || fileLikeByPath
      const resolvedFileFormat = isS3ForSourceFiles
        ? 'FILESYSTEM'
        : isFileLikeSource
          ? 'FILE'
          : String(sourceBase.source_file_format ?? sourceBase.data_source_type ?? 'RDBMS')
            .trim()
            .toUpperCase() || 'RDBMS'
      const resolvedFileType = isS3ForSourceFiles
        ? (sourceExtension || String(sourceBase.source_file_type ?? '').trim())
        : isFileLikeSource
          ? sourceExtension
          : String(sourceBase.source_file_type ?? 'table').trim() || 'table'
      const resolvedPath = isS3ForSourceFiles
        ? (() => {
          // For S3: construct bucket/key JSON using selectedSchema as bucket name
          // and selectedTableName as the key (with extension stripped)
          const bucketName = selectedSchema || 'default-bucket'
          const fullPath = selectedSourceFileName || selectedTableName || ''
          // Remove extension from the key
          const key = stripFileExtension(fullPath) || fullPath
          return JSON.stringify({ bucket: bucketName, key })
        })()
        : isFileLikeSource
          ? removeFileNameFromPath(selectedTablePath, selectedSourceFileName || fileName)
          : String(sourceBase.path ?? '').trim()
      // S3: `table_path` may be empty while object key lives in `path`, so `isFileLikeSource` can be false;
      // always send file_name without extension for S3.
      const payloadFileName = isS3ForSourceFiles
        ? stripFileExtension(selectedSourceFileName || fileName)
        : isFileLikeSource
          ? stripFileExtension(selectedSourceFileName || fileName)
          : fileName

      const registered = await postAdpSourceFiles({
        source_file: [
          {
            data_source_name: dataSourceNameForPayload,
            data_source_instance: dataSourceInstanceForPayload,
            file_name: payloadFileName,
            source_file_format: resolvedFileFormat,
            source_file_type: resolvedFileType,
            authentication_method: toPayloadString(sourceBase.authentication_method),
            payload_query: toPayloadString(sourceBase.payload_query ?? sourceBase.api_body),
            headers: toPayloadString(sourceBase.headers ?? sourceBase.api_headers),
            request_type: toPayloadString(sourceBase.request_type ?? sourceBase.api_method),
            params: toPayloadString(sourceBase.params ?? sourceBase.api_params),
            path: resolvedPath,
            file_delimiter: selectedFileDelimiter,
          },
        ],
        source_file_field: selectedFields.map(col => {
          const cfg = columnConfigs[col.column_name];
          return {
            file_name: payloadFileName,
            field_name: col.column_name,
            field_data_type: cfg.fieldDataType,
            field_structure: '',
            field_size: '0',
            precision: 0,
            is_business_primary_key: cfg.isBusinessPrimaryKey,
            dq_check: buildDqCheckPayload(cfg),
          }
        }),
      })

      setLastRegisteredFields(registered.length > 0 ? registered : null)
      setSaveSourceFileSuccess(true)
      const firstRegistered = registered[0]
      persistSession(
        firstRegistered
          ? {
            rooId: firstRegistered.roo_id,
            sourceDataSourceId: firstRegistered.source_data_source_id,
            sourceFileId: firstRegistered.source_file_id,
          }
          : undefined
      )
      setConfigurationOpen(false);
      setExplorerOpen(false);
      setProfilingOpen(true);
      persistSourceDraftNow({
        selectedTableName,
        tableColumns,
        columnConfigs,
        configurationOpen: false,
        explorerOpen: false,
        profilingOpen: true,
      })
      toast.success('Source registered successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to register source');
      setSaveSourceFileError(err instanceof Error ? err.message : 'Failed to register source')
    } finally {
      setIsSavingSourceFile(false)
    }
  }, [
    allowedInstance,
    columnConfigs,
    configuringNodeId,
    connectorMeta?.instance,
    dataSourceInstanceForPayload,
    dataSourceNameForPayload,
    form.file_delimiter,
    form.file_name,
    connectionPayload,
    isPresignedFileIngestionSource,
    isS3LikeSource,
    registeredSource,
    registeredSourceFilesByTable,
    responseSelections.schema,
    selectedTableName,
    sourceTypeLabel,
    tableColumns,
    persistSourceDraftNow,
  ])

  // ─── DQ modal save ──────────────────────────────────────────────────────────

  const handleDqModalSave = useCallback(() => {
    if (!dqModal) return

    const { columnName, checkType } = dqModal
    const cfg = columnConfigs[columnName]
    if (!cfg) return

    setColumnConfigs(prev => ({
      ...prev,
      [columnName]: {
        ...cfg,
        dqChecks: cfg.dqChecks.includes(checkType) ? cfg.dqChecks : [...cfg.dqChecks, checkType],
        dqModalValues: { ...dqModalDraft },
      },
    }))

    setDqModal(null)
  }, [dqModal, columnConfigs, dqModalDraft])

  // ─── DQ dropdown toggle handler ─────────────────────────────────────────────

  const handleDqDropdownToggle = useCallback(
    (columnName: string) => {
      setOpenDqDropdown(prev => {
        const next = prev === columnName ? null : columnName
        if (next) {
          updateDqDropdownDirection(columnName)
        }
        return next
      })
    },
    [updateDqDropdownDirection]
  )

  const handleDqCheckToggle = useCallback(
    (columnName: string, check: string, cfg: ColumnFieldConfig) => {
      if (DQ_MODAL_CHECKS.includes(check as DqModalType)) {
        if (cfg.dqChecks.includes(check)) {
          // Deselect — no modal needed, just remove

          setColumnConfigs(prev => ({
            ...prev,

            [columnName]: {
              ...cfg,

              dqChecks: cfg.dqChecks.filter(c => c !== check),
            },
          }))
        } else {
          // Open modal to collect values

          setDqModalDraft(cfg.dqModalValues)

          setDqModal({ columnName, checkType: check as DqModalType })

          setOpenDqDropdown(null) // Close dropdown when modal opens
        }
      } else {
        // No modal — just toggle and keep dropdown open

        setColumnConfigs(prev => ({
          ...prev,

          [columnName]: {
            ...cfg,

            dqChecks: cfg.dqChecks.includes(check)
              ? cfg.dqChecks.filter(c => c !== check)
              : [...cfg.dqChecks, check],
          },
        }))
      }
    },
    []
  )

  const handleToggleAllColumns = useCallback(
    (checked: boolean) => {
      setColumnConfigs(prev => {
        const next = { ...prev }

        for (const column of tableColumns) {
          const existing = prev[column.column_name] ?? {
            selected: true,
            fieldDataType: column.data_type,
            isBusinessPrimaryKey: false,
            dqChecks: [],
            dqModalValues: initialDqModalValues,
          }

          next[column.column_name] = {
            ...existing,
            selected: checked,
          }
        }

        return next
      })
    },
    [tableColumns]
  )

  const canSaveDqModal = useMemo(() => {
    if (!dqModal) return false

    switch (dqModal.checkType) {
      case 'Range Check':
        return dqModalDraft.rangeMin.trim().length > 0 && dqModalDraft.rangeMax.trim().length > 0
      case 'Isin Set':
        return dqModalDraft.isinValue.trim().length > 0
      case 'Length Check':
        return dqModalDraft.lengthValue.trim().length > 0
      case 'Format Check':
        return dqModalDraft.formatValue.trim().length > 0
      default:
        return false
    }
  }, [dqModal, dqModalDraft])

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <aside className={styles.root} aria-label="Configuration details">
      <div className={styles.inner}>
        <div className={styles.headerRow}>
          <button
            type="button"
            className={styles.closeChevron}
            onClick={onClose}
            aria-label="Close"
          >
            <img
              src={chevronDoubleRightIcon}
              alt=""
              className={styles.closeChevronIcon}
              aria-hidden
            />
          </button>

          <h2 className={styles.title}>Configuration Details</h2>
        </div>

        <button type="button" className={styles.backLink} onClick={onBackToChoose}>
          ← Back to source list
        </button>

        <div className={styles.divider} />

        <p className={styles.subtitle}>
          {isRegisteredSourceSelected
            ? 'Configuration details are locked for this registered source.'
            : 'Enter the configuration details for the source and check the data quality.'}
        </p>

        <div className={styles.summaryCard}>
          <div className={styles.summaryIcon}>
            <img
              src={displayImage}
              alt={displayTitle}
            />
          </div>

          <div className={styles.summaryBody}>
            <div className={styles.summaryTitle}>{displayTitle}</div>

            <div className={styles.summaryType}>Source Type: {displayTypeLabel}</div>

            <button
              type="button"
              className={styles.prereqLink}
              onClick={() => setPrereqModalOpen(true)}
            >
              <MaterialIcon name="warning" size={16} aria-hidden />
              Prerequisites
            </button>
          </div>
        </div>

        {/* ── Configuration section ─────────────────────────────────────────── */}

        {configurationOpen ? (
          <>
            {isRegisteredSourceSelected ? (
              <RegisteredSourceDetailsCard
                registeredSource={registeredSource}
                onAddNew={handleRegisteredAddNewTable}
                onSaveExistingSelection={handleRegisteredExistingSave}
                isSavingSelection={isSavingSourceFile}
              />
            ) : (
              <div className={styles.formCard}>
                {showDynamicConfigSkeleton ? (
                  <DynamicConfigFormSkeleton />
                ) : (
                  dynamicConfigFields.map(field => {
                    const key = field.key ?? ''
                    const fieldType =
                      isPresignedFileIngestionSource && key === 'file_name' ? 'file' : field.type
                    if (!key) return null
                    const label = field.label ?? key
                    const required = field.validators?.includes('required') ?? false
                    return (
                      <FieldRow
                        key={key}
                        fieldKey={key}
                        label={label}
                        value={form[key] ?? ''}
                        onChange={v => {
                          setForm(f => ({ ...f, [key]: v }))
                          if (validationErrors[key]) {
                            setValidationErrors(prev => {
                              const next = { ...prev }
                              delete next[key]
                              return next
                            })
                          }
                        }}
                        onFilePicked={
                          isPresignedFileIngestionSource && key === 'file_name'
                            ? file => {
                              excelPickedFileRef.current = file
                              if (file) {
                                if (isDelimitedTextFileName(file.name)) {
                                  setForm(f => ({
                                    ...f,
                                    file_delimiter: f.file_delimiter?.trim() ? f.file_delimiter : ',',
                                  }))
                                } else {
                                  setForm(f => ({ ...f, file_delimiter: '' }))
                                  setExcelCustomDelimiter('')
                                }
                                const r = new FileReader()
                                r.onload = () => {
                                  const result = r.result
                                  if (typeof result === 'string') {
                                    try {
                                      globalThis.sessionStorage.setItem('excel_file', result)
                                    } catch {
                                      /* quota / private mode */
                                    }
                                  }
                                }
                                r.readAsDataURL(file)
                              } else {
                                setForm(f => ({ ...f, file_delimiter: '' }))
                                setExcelCustomDelimiter('')
                                try {
                                  globalThis.sessionStorage.removeItem('excel_file')
                                } catch {
                                  /* */
                                }
                              }
                            }
                            : undefined
                        }
                        infoLabel={field.toolTip || `About ${label}`}
                        placeholder={field.placeholder}
                        type={fieldType}
                        options={field.options}
                        optional={!required}
                        disabled={connectionFieldsLocked}
                        error={validationErrors[key]}
                      />
                    )
                  })
                )}
                {showInjectedFileDelimiter && !isS3LikeSource && (
                  <div className={styles.field}>
                    <div className={styles.fieldLabel}>
                      <span>Delimiter</span>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" className={styles.infoBtn} aria-label="About delimiter">
                              <MaterialIcon name="info" size={16} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            Delimiter for CSV/TSV/TXT (matches presigned databases file_delimiter and
                            showTables). Use Custom for a character or string (e.g. $).
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <select
                      className={styles.textInput}
                      value={form.file_delimiter ?? ''}
                      disabled={!delimiterControlEnabled}
                      onChange={e => {
                        const v = e.target.value
                        setForm(f => ({ ...f, file_delimiter: v }))
                        if (v !== CUSTOM_FILE_DELIMITER) {
                          setExcelCustomDelimiter('')
                        }
                        if (validationErrors.file_delimiter) {
                          setValidationErrors(prev => {
                            const n = { ...prev }
                            delete n.file_delimiter
                            return n
                          })
                        }
                      }}
                    >
                      <option value="">Select delimiter</option>
                      {FILE_DELIMITER_CHOICES.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {!isS3LikeSource &&
                      !isDelimitedTextFileName((form.file_name ?? selectedTableName ?? '').trim()) && (
                        <p className={styles.fieldHint}>
                          Choose a .csv, .txt, or .tsv file above to set the delimiter.
                        </p>
                      )}
                    {(form.file_delimiter ?? '').trim() === CUSTOM_FILE_DELIMITER && (
                      <input
                        className={styles.textInput}
                        type="text"
                        style={{ marginTop: 8 }}
                        placeholder="Enter custom delimiter (e.g. $ or %)"
                        value={excelCustomDelimiter}
                        disabled={!delimiterControlEnabled}
                        onChange={e => setExcelCustomDelimiter(e.target.value)}
                        autoComplete="off"
                      />
                    )}
                  </div>
                )}
                {showInjectedFileDateFormat && !isS3LikeSource && (
                  <div className={styles.field}>
                    <div className={styles.fieldLabel}>
                      <span>Date Format</span>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" className={styles.infoBtn} aria-label="About date format">
                              <MaterialIcon name="info" size={16} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            Date format for parsing source file date columns (e.g. dd-mm-yyyy).
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Select
                      value={form.file_date_format ?? form.date_format ?? EMPTY_SELECT_VALUE}
                      disabled={connectionFieldsLocked}
                      onValueChange={nextValue => {
                        const v = nextValue === EMPTY_SELECT_VALUE ? '' : nextValue
                        setForm(f => ({ ...f, file_date_format: v, date_format: v }))
                        if (validationErrors.file_date_format || validationErrors.date_format) {
                          setValidationErrors(prev => {
                            const n = { ...prev }
                            delete n.file_date_format
                            delete n.date_format
                            return n
                          })
                        }
                      }}
                    >
                      <SelectTrigger className={styles.textInput}>
                        <SelectValue placeholder="Select date format" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={EMPTY_SELECT_VALUE}>Select date format</SelectItem>
                        {FILE_DATE_FORMAT_CHOICES.map(opt => (
                          <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {schemaTestFields.map(field => (
                  <div key={field.key} className={styles.field}>
                    <div className={styles.fieldLabel}>{toDisplayFieldKeyLabel(field.key)}</div>
                    <Select
                      value={responseSelections[field.key] || EMPTY_SELECT_VALUE}
                      onValueChange={nextValue => {
                        const resolvedValue = nextValue === EMPTY_SELECT_VALUE ? '' : nextValue
                        setResponseSelections(prev => ({ ...prev, [field.key]: resolvedValue }))
                        setDiscoveredTables([])
                        setSelectedTableName('')
                        setTableColumns([])
                      }}
                    >
                      <SelectTrigger className={styles.textInput}>
                        <SelectValue
                          placeholder={field.placeholder || `Select ${toDisplayFieldKeyLabel(field.key)}`}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={EMPTY_SELECT_VALUE}>
                          {field.placeholder || `Select ${toDisplayFieldKeyLabel(field.key)}`}
                        </SelectItem>
                        {(field.options ?? []).map(option => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}

            {!isRegisteredSourceSelected && !showDynamicConfigSkeleton && schemaTestFields.length === 0 && (
              <div className={styles.testRow}>
                <button
                  type="button"
                  className={styles.testBtn}
                  onClick={handleTest}
                  disabled={isTesting || connectionFieldsLocked}
                >
                  {isTesting
                    ? (isPresignedFileIngestionSource ? 'Saving...' : 'Testing...')
                    : (isPresignedFileIngestionSource ? 'Save' : 'Test')}
                </button>
              </div>
            )}

            {!isRegisteredSourceSelected && registeredSource && registeredSourceFilesLoadError && (
              <p className={styles.errorText} role="alert">
                {registeredSourceFilesLoadError}{' '}
                {isPresignedFileIngestionSource
                  ? 'Use Save to connect manually, or fix the source registration.'
                  : 'Use Test to connect manually, or fix the source registration.'}
              </p>
            )}

            {!isRegisteredSourceSelected && !showDynamicConfigSkeleton && schemaTestFields.length > 0 && (
              <div className={styles.nextRow}>
                <Button
                  type="button"
                  onClick={() =>
                    void handleSchemaSelection(primaryTestFieldKey ?? '', selectedSchema)
                  }
                  disabled={!primaryTestFieldKey || !selectedSchema || isSchemaLoading}
                >
                  {isSchemaLoading ? 'Loading…' : 'Save'}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className={styles.collapsedRow}>
            <button
              type="button"
              className={styles.expandAllBtn}
              onClick={() => setConfigurationOpen(true)}
            >
              <img
                src={chevronDoubleRightIcon}
                alt=""
                className={styles.expandAllBtnIcon}
                aria-hidden
              />
              Configuration Details
            </button>
          </div>
        )}

        {/* ── Accordion: Explorer + Data Profiling ──────────────────────────── */}

        <div className={styles.accordionBlock}>
          <div className={styles.expandAllRow}>
            <button type="button" className={styles.expandAllBtn} onClick={handleExpandAll}>
              <MaterialIcon name="unfold_more" size={18} aria-hidden />

              {allExpanded ? 'Collapse all' : 'Expand All'}
            </button>
          </div>

          {/* Explorer */}
          {!hideExplorerForRegisteredSelection && (
            <div className={styles.accordionCard}>
              <button
                type="button"
                className={`${styles.accordionItem} ${styles.accordionItemEmbedded}`}
                onClick={() => setExplorerOpen(o => !o)}
                aria-expanded={explorerOpen}
              >
                Explorer
                <MaterialIcon
                  name={explorerOpen ? 'expand_more' : 'chevron_right'}
                  size={20}
                  className={styles.accordionChevron}
                  aria-hidden
                />
              </button>

              {explorerOpen && (
                <div className={`${styles.explorerPanel} ${styles.accordionPanel}`}>
                  {explorerExtraFields.map(field => (
                    <div key={field.key} className={styles.field}>
                      <div className={styles.fieldLabel}>{toDisplayFieldKeyLabel(field.key)}</div>

                      <select
                        className={styles.textInput}
                        value={responseSelections[field.key] ?? ''}
                        onChange={e =>
                          setResponseSelections(prev => ({ ...prev, [field.key]: e.target.value }))
                        }
                      >
                        <option value="">
                          {field.placeholder || `Select ${toDisplayFieldKeyLabel(field.key)}`}
                        </option>

                        {(field.options ?? []).map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}

                  {showInjectedFileDelimiter && isS3LikeSource && s3ShowFileParsingInExplorer && (
                    <div className={styles.field}>
                      <div className={styles.fieldLabel}>
                        <span>Delimiter</span>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className={styles.infoBtn} aria-label="About delimiter">
                                <MaterialIcon name="info" size={16} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              Delimiter for CSV/TSV/TXT (matches presigned databases file_delimiter and
                              showTables). Use Custom for a character or string (e.g. $).
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <select
                        className={styles.textInput}
                        value={form.file_delimiter ?? ''}
                        disabled={isS3LikeSource ? !s3ShowFileParsingInExplorer : !delimiterControlEnabled}
                        onChange={e => {
                          const v = e.target.value
                          setForm(f => ({ ...f, file_delimiter: v }))
                          if (v !== CUSTOM_FILE_DELIMITER) {
                            setExcelCustomDelimiter('')
                          }
                          if (validationErrors.file_delimiter) {
                            setValidationErrors(prev => {
                              const n = { ...prev }
                              delete n.file_delimiter
                              return n
                            })
                          }
                        }}
                      >
                        <option value="">Select delimiter</option>
                        {FILE_DELIMITER_CHOICES.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {(form.file_delimiter ?? '').trim() === CUSTOM_FILE_DELIMITER && (
                        <input
                          className={styles.textInput}
                          type="text"
                          style={{ marginTop: 8 }}
                          placeholder="Enter custom delimiter (e.g. $ or %)"
                          value={excelCustomDelimiter}
                          disabled={isS3LikeSource ? !s3ShowFileParsingInExplorer : !delimiterControlEnabled}
                          onChange={e => setExcelCustomDelimiter(e.target.value)}
                          autoComplete="off"
                        />
                      )}
                    </div>
                  )}
                  {showInjectedFileDateFormat && isS3LikeSource && s3ShowFileParsingInExplorer && (
                    <div className={styles.field}>
                      <div className={styles.fieldLabel}>
                        <span>Date Format</span>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className={styles.infoBtn} aria-label="About date format">
                                <MaterialIcon name="info" size={16} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              Date format for parsing source file date columns (e.g. dd-mm-yyyy).
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <select
                        className={styles.textInput}
                        value={form.file_date_format ?? form.date_format ?? ''}
                        disabled={isS3LikeSource ? !s3ShowFileParsingInExplorer : connectionFieldsLocked}
                        onChange={e => {
                          const v = e.target.value
                          setForm(f => ({ ...f, file_date_format: v, date_format: v }))
                          if (validationErrors.file_date_format || validationErrors.date_format) {
                            setValidationErrors(prev => {
                              const n = { ...prev }
                              delete n.file_date_format
                              delete n.date_format
                              return n
                            })
                          }
                        }}
                      >
                        <option value="">Select date format</option>
                        {FILE_DATE_FORMAT_CHOICES.map(opt => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {(isSchemaLoading || isLoadingRegisteredSourceFiles) && (
                    <p className={styles.subtitle}>Loading tables…</p>
                  )}

                  {!isSchemaLoading && discoveredTables.length > 0 && (
                    <div className={styles.tablesWrap}>
                      <div className={styles.field}>
                        <div className={styles.fieldLabel}>
                          <span>Connection</span>
                        </div>

                        <select className={styles.textInput} value={normalizedType} disabled>
                          <option value={normalizedType}>{normalizedType.toUpperCase()}</option>
                        </select>
                      </div>

                      <div className={styles.field}>
                        <div className={styles.fieldLabel}>
                          <span>Table Name</span>
                        </div>

                        <select
                          className={styles.textInput}
                          value={selectedTableName}
                          onChange={e => void handleTableSelection(e.target.value)}
                        >
                          <option value="">Select Table</option>

                          {discoveredTables.map(tableName => (
                            <option key={tableName} value={tableName}>
                              {tableName}
                            </option>
                          ))}
                        </select>
                      </div>

                      {isColumnsLoading && <p className={styles.subtitle}>Loading columns…</p>}

                      {!isColumnsLoading && tableColumns.length > 0 && (
                        <>
                          <div className={styles.columnsHeader}>
                            <span>
                              <input
                                type="checkbox"
                                checked={allColumnsSelected}
                                aria-label="Select all columns"
                                onChange={e => handleToggleAllColumns(e.target.checked)}
                              />
                            </span>

                            <span>Field Name</span>

                            <span>Data Type</span>

                            <span>Primary Key</span>

                            <span>Data Quality</span>
                          </div>

                          {tableColumns.map(column => {
                            const cfg = columnConfigs[column.column_name] ?? {
                              selected: true,

                              fieldDataType: column.data_type,

                              isBusinessPrimaryKey: false,

                              dqChecks: [],

                              dqModalValues: initialDqModalValues,
                            }

                            return (
                              <div key={column.column_name} className={styles.columnsRow}>
                                <input
                                  type="checkbox"
                                  checked={cfg.selected}
                                  aria-label={`Select ${column.column_name}`}
                                  onChange={e =>
                                    setColumnConfigs(prev => ({
                                      ...prev,

                                      [column.column_name]: { ...cfg, selected: e.target.checked },
                                    }))
                                  }
                                />

                                <span>{column.column_name}</span>

                                <select
                                  className={styles.compactSelect}
                                  value={cfg.fieldDataType}
                                  onChange={e =>
                                    setColumnConfigs(prev => ({
                                      ...prev,

                                      [column.column_name]: { ...cfg, fieldDataType: e.target.value },
                                    }))
                                  }
                                >
                                  {dataTypeSelectOptions(column.data_type).map(opt => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>

                                <input
                                  type="checkbox"
                                  checked={cfg.isBusinessPrimaryKey}
                                  aria-label={`Primary key ${column.column_name}`}
                                  onChange={e =>
                                    setColumnConfigs(prev => ({
                                      ...prev,

                                      [column.column_name]: {
                                        ...cfg,
                                        isBusinessPrimaryKey: e.target.checked,
                                      },
                                    }))
                                  }
                                />

                                {/* ── Multi-select DQ checks dropdown ───────────────── */}

                                <div
                                  className={styles.dqMultiWrap}
                                  ref={node => {
                                    if (node) {
                                      dropdownRefs.current.set(column.column_name, node)
                                    } else {
                                      dropdownRefs.current.delete(column.column_name)
                                    }
                                  }}
                                >
                                  <button
                                    type="button"
                                    className={styles.dqTrigger}
                                    onClick={() => handleDqDropdownToggle(column.column_name)}
                                  >
                                    <span className={styles.dqTriggerText}>
                                      {cfg.dqChecks.length === 0
                                        ? 'Select Data Quality'
                                        : cfg.dqChecks.join(', ')}
                                    </span>

                                    <MaterialIcon
                                      name={
                                        openDqDropdown === column.column_name
                                          ? 'expand_less'
                                          : 'expand_more'
                                      }
                                      size={16}
                                    />
                                  </button>

                                  {openDqDropdown === column.column_name && (
                                    <div
                                      className={`${styles.dqDropdown} ${dqDropdownDirectionByColumn[column.column_name] === 'up'
                                        ? styles.dqDropdownUp
                                        : ''
                                        }`}
                                    >
                                      {DQ_CHECK_OPTIONS.map(check => (
                                        <label key={check} className={styles.dqCheckItem}>
                                          <input
                                            type="checkbox"
                                            checked={cfg.dqChecks.includes(check)}
                                            onChange={() =>
                                              handleDqCheckToggle(column.column_name, check, cfg)
                                            }
                                          />

                                          <span>{check}</span>
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          })}

                          <div className={styles.testRow}>
                            <button
                              type="button"
                              className={styles.testBtn}
                              onClick={() => void handleSaveSourceFile()}
                              disabled={
                                isSavingSourceFile || !selectedTableName || tableColumns.length === 0
                              }
                            >
                              {isSavingSourceFile ? 'Saving…' : 'Save'}
                            </button>
                          </div>

                          {saveSourceFileError && (
                            <p className={styles.errorText} role="alert">
                              {saveSourceFileError}
                            </p>
                          )}

                          {saveSourceFileSuccess && !saveSourceFileError && (
                            <p className={styles.subtitle} role="status">
                              Source file saved successfully.
                            </p>
                          )}
                        </>
                      )}

                      {!isColumnsLoading && selectedTableName && tableColumns.length === 0 && (
                        <div className={styles.tablesRow}>
                          <span />

                          <span>No columns found for selected table.</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Data Profiling */}
          <div className={styles.accordionCard}>
            <button
              type="button"
              className={`${styles.accordionItem} ${styles.accordionItemEmbedded}`}
              onClick={() => setProfilingOpen(o => !o)}
              aria-expanded={profilingOpen}
            >
              Data Profiling
              <MaterialIcon
                name={profilingOpen ? 'expand_more' : 'chevron_right'}
                size={20}
                className={styles.accordionChevron}
                aria-hidden
              />
            </button>

            {profilingOpen && (
              <div className={`${styles.profilingPanel} ${styles.accordionPanel}`}>
                <div className={styles.profilingPrompt}>
                  <span>Continue with Data Profiling?</span>

                  <label className={styles.choiceLabel}>
                    <input
                      type="radio"
                      name="profiling-choice"
                      checked={profilingChoice === 'yes'}
                      onChange={() => {
                        setProfilingChoice('yes')
                        setProfilingRequestNonce(v => v + 1)
                        persistSourceDraftNow({ profilingOpen: true })
                      }}
                    />
                    Yes
                  </label>

                  <label className={styles.choiceLabel}>
                    <input
                      type="radio"
                      name="profiling-choice"
                      checked={profilingChoice === 'no'}
                      onChange={() => {
                        setProfilingChoice('no')

                        setProfilingPipelineError(null)

                        setProfilingPipelineSuccess(false)

                        setProfilingDqStatRows([])

                        setProfilingWsStatus(null)

                        profilingRunIdRef.current += 1

                        setProfilingOpen(false)
                        persistSourceDraftNow({ profilingOpen: false })
                      }}
                    />
                    No
                  </label>
                </div>

                {profilingChoice === 'yes' && (
                  <div className={styles.profilingTableWrap}>
                    {profilingPipelineError && (
                      <p className={styles.errorText} role="alert">
                        {profilingPipelineError}
                      </p>
                    )}

                    {profilingPipelineSuccess && !profilingPipelineError && (
                      <p className={styles.subtitle} role="status">
                        Data profiling completed.
                      </p>
                    )}

                    <div className={styles.profilingScrollWrap}>
                      {profilingPipelineLoading ? (
                        <div className={styles.profilingTableLoader}>
                          <Loader
                            text={
                              profilingWsStatus
                                ? 'Data Profiling in progress...'
                                : 'Data Profiling in progress...'
                            }
                          />
                        </div>
                      ) : (
                        <table className={styles.profilingTable}>
                          <thead>
                            <tr>
                              <th className={styles.profilingTh}>Name</th>

                              <th className={styles.profilingTh}>Data Type</th>

                              <th className={styles.profilingTh}>Distinct Values</th>

                              <th className={styles.profilingTh}>Non Distinct Values</th>

                              <th className={styles.profilingTh}>Null Values</th>

                              <th className={styles.profilingTh}>Expand</th>
                            </tr>
                          </thead>

                          <tbody>
                            {profilingDisplayRows.length === 0 &&
                              !profilingPipelineLoading &&
                              !profilingPipelineError && (
                                <tr>
                                  <td colSpan={6} className={styles.profilingEmptyCell}>
                                    Register the source in Explorer (table + fields) to run
                                    profiling.
                                  </td>
                                </tr>
                              )}

                            {profilingDisplayRows.map(row => {
                              const isExpanded = expandedProfilingFieldId === row.key

                              return (
                                <React.Fragment key={row.key}>
                                  <tr className={styles.profilingTr}>
                                    <td className={styles.profilingTd}>
                                      <span className={styles.profilingFieldName}>
                                        {row.fieldName}
                                      </span>
                                    </td>

                                    <td className={styles.profilingTd}>
                                      {row.fieldDataType && row.fieldDataType !== '—' ? (
                                        <span className={styles.typePill}>
                                          • {row.fieldDataType}
                                        </span>
                                      ) : (
                                        <span className={styles.profilingDash}>—</span>
                                      )}
                                    </td>

                                    <td className={styles.profilingTd}>
                                      {profilingDistinctDisplay(row.stats)}
                                    </td>

                                    <td className={styles.profilingTd}>
                                      {profilingNonDistinctDisplay(row.stats)}
                                    </td>

                                    <td className={styles.profilingTd}>
                                      {profilingNullDisplay(row.stats)}
                                    </td>

                                    <td className={styles.profilingTd}>
                                      <button
                                        type="button"
                                        className={styles.expandMiniBtn}
                                        aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                                        onClick={() =>
                                          setExpandedProfilingFieldId(prev =>
                                            prev === row.key ? null : row.key
                                          )
                                        }
                                      >
                                        <MaterialIcon
                                          name={isExpanded ? 'expand_less' : 'expand_more'}
                                          size={18}
                                        />
                                      </button>
                                    </td>
                                  </tr>

                                  {isExpanded &&
                                    (() => {
                                      const stats = row.stats
                                      const distinctCount = coerceFiniteNumber(
                                        stats?.distinct_count
                                      )
                                      const totalCount = coerceFiniteNumber(stats?.total_count)
                                      const nullCount = coerceFiniteNumber(stats?.null_value)
                                      const nonDistinctCount =
                                        stats?.duplicate != null
                                          ? coerceFiniteNumber(stats.duplicate)
                                          : Math.max(0, totalCount - distinctCount - nullCount)
                                      const distinctPct =
                                        stats?.distinct_percent != null
                                          ? coerceFiniteNumber(stats.distinct_percent)
                                          : totalCount > 0
                                            ? (distinctCount / totalCount) * 100
                                            : 0
                                      const nullPct =
                                        stats?.null_percent != null
                                          ? coerceFiniteNumber(stats.null_percent)
                                          : totalCount > 0
                                            ? (nullCount / totalCount) * 100
                                            : 0
                                      const nonDistinctPct = Math.max(
                                        0,
                                        100 - distinctPct - nullPct
                                      )

                                      const rawDate = stats?.created_datetime_timestamp
                                      const dateStr = rawDate
                                        ? (() => {
                                          try {
                                            return new Date(String(rawDate)).toLocaleString()
                                          } catch {
                                            return String(rawDate)
                                          }
                                        })()
                                        : null

                                      const minVal = formatProfilingDetailValue(stats?.min_value)
                                      const maxVal = formatProfilingDetailValue(stats?.max_value)
                                      const meanVal = formatProfilingDetailValue(stats?.mean)
                                      const stdDevVal = formatProfilingDetailValue(
                                        stats?.standard_deviation
                                      )
                                      const medianVal = formatProfilingDetailValue(stats?.median)

                                      const hasDistribution =
                                        stats?.min_value != null &&
                                        stats?.max_value != null &&
                                        stats?.mean != null

                                      return (
                                        <tr className={styles.profilingDetailTr}>
                                          <td colSpan={6} className={styles.profilingDetailTd}>
                                            <div className={styles.pdcCard}>
                                              {/* ── Header ── */}
                                              <div className={styles.pdcHeader}>
                                                <span className={styles.pdcFieldName}>
                                                  {row.fieldName}
                                                </span>
                                                {dateStr && (
                                                  <span className={styles.pdcDate}>
                                                    At {dateStr}
                                                  </span>
                                                )}
                                              </div>

                                              {/* ── Rows section ── */}
                                              <div className={styles.pdcRowsSection}>
                                                <div className={styles.pdcSectionTitle}>Rows</div>

                                                <div className={styles.pdcRowItem}>
                                                  <span
                                                    className={`${styles.pdcSwatch} ${styles.pdcSwatchDistinct}`}
                                                  />
                                                  <span className={styles.pdcRowLabel}>
                                                    Distinct values
                                                  </span>
                                                  <span className={styles.pdcRowCount}>
                                                    {distinctCount}
                                                  </span>
                                                  <span className={styles.pdcRowPct}>
                                                    {distinctPct.toFixed(1)}%
                                                  </span>
                                                  <div className={styles.pdcMiniBarTrack}>
                                                    <div
                                                      className={`${styles.pdcMiniBar} ${styles.pdcMiniBarDistinct}`}
                                                      style={{
                                                        width: `${Math.min(distinctPct, 100)}%`,
                                                      }}
                                                    />
                                                  </div>
                                                </div>

                                                <div className={styles.pdcRowItem}>
                                                  <span
                                                    className={`${styles.pdcSwatch} ${styles.pdcSwatchNonDistinct}`}
                                                  />
                                                  <span className={styles.pdcRowLabel}>
                                                    Non distinct values
                                                  </span>
                                                  <span className={styles.pdcRowCount}>
                                                    {nonDistinctCount}
                                                  </span>
                                                  <span className={styles.pdcRowPct}>
                                                    {nonDistinctPct.toFixed(1)}%
                                                  </span>
                                                  <div className={styles.pdcMiniBarTrack}>
                                                    <div
                                                      className={`${styles.pdcMiniBar} ${styles.pdcMiniBarNonDistinct}`}
                                                      style={{
                                                        width: `${Math.min(nonDistinctPct, 100)}%`,
                                                      }}
                                                    />
                                                  </div>
                                                </div>

                                                <div
                                                  className={`${styles.pdcRowItem} ${nullCount === 0 ? styles.pdcRowItemMuted : ''}`}
                                                >
                                                  <span
                                                    className={`${styles.pdcSwatch} ${styles.pdcSwatchNull}`}
                                                  />
                                                  <span className={styles.pdcRowLabel}>
                                                    Null values
                                                  </span>
                                                  <span className={styles.pdcRowCount}>
                                                    {nullCount}
                                                  </span>
                                                  <span className={styles.pdcRowPct}>
                                                    {nullPct.toFixed(1)}%
                                                  </span>
                                                  <div className={styles.pdcMiniBarTrack} />
                                                </div>

                                                <div className={styles.pdcTotalRow}>
                                                  <span className={styles.pdcTotalLabel}>
                                                    Total Rows
                                                  </span>
                                                  <span className={styles.pdcTotalCount}>
                                                    {totalCount}
                                                  </span>
                                                </div>
                                              </div>

                                              {/* ── Stacked bar chart ── */}
                                              <div className={styles.pdcChartSection}>
                                                <div className={styles.pdcScaleRow}>
                                                  {[
                                                    '0%',
                                                    '10%',
                                                    '20%',
                                                    '30%',
                                                    '40%',
                                                    '50%',
                                                    '60%',
                                                    '70%',
                                                    '80%',
                                                    '90%',
                                                    '100%',
                                                  ].map(t => (
                                                    <span key={t} className={styles.pdcScaleTick}>
                                                      {t}
                                                    </span>
                                                  ))}
                                                </div>
                                                <div className={styles.pdcStackedBar}>
                                                  <div
                                                    className={styles.pdcStackDistinct}
                                                    style={{
                                                      width: `${Math.min(distinctPct, 100)}%`,
                                                    }}
                                                  />
                                                  <div
                                                    className={styles.pdcStackNonDistinct}
                                                    style={{
                                                      width: `${Math.min(nonDistinctPct, 100)}%`,
                                                    }}
                                                  />
                                                  {nullPct > 0 && (
                                                    <div
                                                      className={styles.pdcStackNull}
                                                      style={{
                                                        width: `${Math.min(nullPct, 100)}%`,
                                                      }}
                                                    />
                                                  )}
                                                </div>
                                                <div className={styles.pdcLegend}>
                                                  <span className={styles.pdcLegendItem}>
                                                    <span
                                                      className={`${styles.pdcLegendSwatch} ${styles.pdcLegendSwatchDistinct}`}
                                                    />
                                                    Distinct values ({distinctPct.toFixed(1)}%)
                                                  </span>
                                                  <span className={styles.pdcLegendItem}>
                                                    <span
                                                      className={`${styles.pdcLegendSwatch} ${styles.pdcLegendSwatchNonDistinct}`}
                                                    />
                                                    Non distinct values ({nonDistinctPct.toFixed(1)}
                                                    %)
                                                  </span>
                                                  {nullPct > 0 && (
                                                    <span className={styles.pdcLegendItem}>
                                                      <span
                                                        className={`${styles.pdcLegendSwatch} ${styles.pdcLegendSwatchNull}`}
                                                      />
                                                      Null values ({nullPct.toFixed(1)}%)
                                                    </span>
                                                  )}
                                                </div>
                                              </div>

                                              {/* ── Values + Statistics ── */}
                                              <div className={styles.pdcStatsGrid}>
                                                <div className={styles.pdcStatsCol}>
                                                  <div className={styles.pdcColTitle}>Values</div>
                                                  <div className={styles.pdcStatRow}>
                                                    <span className={styles.pdcStatLabel}>Min</span>
                                                    <span className={styles.pdcStatValue}>
                                                      {minVal}
                                                    </span>
                                                  </div>
                                                  <div className={styles.pdcStatRow}>
                                                    <span className={styles.pdcStatLabel}>Max</span>
                                                    <span className={styles.pdcStatValue}>
                                                      {maxVal}
                                                    </span>
                                                  </div>
                                                  <div className={styles.pdcStatRow}>
                                                    <span className={styles.pdcStatLabel}>
                                                      Distinct Values
                                                    </span>
                                                    <span className={styles.pdcStatValue}>
                                                      {distinctCount !== 0 ? distinctCount : '—'}
                                                    </span>
                                                  </div>
                                                </div>
                                                <div className={styles.pdcStatsCol}>
                                                  <div className={styles.pdcColTitle}>
                                                    Statistics
                                                  </div>
                                                  <div className={styles.pdcStatRow}>
                                                    <span
                                                      className={`${styles.pdcStatLabel} ${styles.pdcStatLabelAccent}`}
                                                    >
                                                      Mean
                                                    </span>
                                                    <span className={styles.pdcStatValue}>
                                                      {meanVal}
                                                    </span>
                                                  </div>
                                                  <div className={styles.pdcStatRow}>
                                                    <span
                                                      className={`${styles.pdcStatLabel} ${styles.pdcStatLabelAccent}`}
                                                    >
                                                      Standard deviation
                                                    </span>
                                                    <span className={styles.pdcStatValue}>
                                                      {stdDevVal}
                                                    </span>
                                                  </div>
                                                  <div className={styles.pdcStatRow}>
                                                    <span
                                                      className={`${styles.pdcStatLabel} ${styles.pdcStatLabelAccent}`}
                                                    >
                                                      Median
                                                    </span>
                                                    <span className={styles.pdcStatValue}>
                                                      {medianVal}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>

                                              {/* ── Distribution chart ── */}
                                              {hasDistribution && (
                                                <div className={styles.pdcDistSection}>
                                                  <div className={styles.pdcDistTrackWrap}>
                                                    <div className={styles.pdcDistTrack}>
                                                      <div
                                                        className={`${styles.pdcDistMarker} ${styles.pdcDistMarkerLeft}`}
                                                      >
                                                        <div className={styles.pdcDistMarkerLine} />
                                                      </div>
                                                      <div
                                                        className={`${styles.pdcDistMarker} ${styles.pdcDistMarkerCenter}`}
                                                      >
                                                        <div className={styles.pdcDistMarkerLine} />
                                                      </div>
                                                      <div
                                                        className={`${styles.pdcDistMarker} ${styles.pdcDistMarkerRight}`}
                                                      >
                                                        <div className={styles.pdcDistMarkerLine} />
                                                      </div>
                                                    </div>
                                                  </div>
                                                  <div className={styles.pdcDistLabels}>
                                                    <div className={styles.pdcDistLabelItem}>
                                                      <span className={styles.pdcDistLabelValue}>
                                                        {minVal}
                                                      </span>
                                                      <span className={styles.pdcDistLabelSub}>
                                                        Min
                                                      </span>
                                                    </div>
                                                    <div
                                                      className={`${styles.pdcDistLabelItem} ${styles.pdcDistLabelCenter}`}
                                                    >
                                                      <span className={styles.pdcDistLabelValue}>
                                                        {meanVal}
                                                      </span>
                                                      <span className={styles.pdcDistLabelSub}>
                                                        Mean
                                                      </span>
                                                    </div>
                                                    <div
                                                      className={`${styles.pdcDistLabelItem} ${styles.pdcDistLabelRight}`}
                                                    >
                                                      <span className={styles.pdcDistLabelValue}>
                                                        {maxVal}
                                                      </span>
                                                      <span className={styles.pdcDistLabelSub}>
                                                        Max
                                                      </span>
                                                    </div>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )
                                    })()}
                                </React.Fragment>
                              )
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {!profilingPipelineLoading && (
                      <div className={styles.profilingFooter}>
                        <div className={styles.paginationInfo}>
                          {profilingDisplayRows.length} field
                          {profilingDisplayRows.length === 1 ? '' : 's'}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── DQ Check Modal ──────────────────────────────────────────────────── */}

      <Dialog
        open={!!dqModal}
        onOpenChange={open => {
          if (!open) setDqModal(null)
        }}
      >
        <DialogContent
          className={styles.dqDialogContent}
          onOpenAutoFocus={event => {
            event.preventDefault()
          }}
        >
          <DialogHeader>
            <DialogTitle className={styles.dqDialogTitle}>
              {dqModal?.checkType}

              {dqModal?.checkType && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className={styles.dqDialogInfo} aria-label="Info">
                        <MaterialIcon name="info" size={16} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {DQ_MODAL_TOOLTIP_MESSAGES[dqModal.checkType]}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className={styles.dqDialogRule} />

          <div className={styles.dqDialogBody}>
            {dqModal?.checkType === 'Range Check' && (
              <div className={styles.dqRangeRow}>
                <div className={styles.dqField}>
                  <label className={styles.dqLabel}>Min</label>

                  <input
                    type="text"
                    className={styles.dqInput}
                    placeholder="Enter values"
                    value={dqModalDraft.rangeMin}
                    onChange={e => setDqModalDraft(d => ({ ...d, rangeMin: e.target.value }))}
                  />
                </div>

                <div className={styles.dqField}>
                  <label className={styles.dqLabel}>Max</label>

                  <input
                    type="text"
                    className={styles.dqInput}
                    placeholder="Enter values"
                    value={dqModalDraft.rangeMax}
                    onChange={e => setDqModalDraft(d => ({ ...d, rangeMax: e.target.value }))}
                  />
                </div>
              </div>
            )}

            {dqModal?.checkType === 'Isin Set' && (
              <div className={styles.dqField}>
                <label className={styles.dqLabel}>Value</label>

                <input
                  type="text"
                  className={styles.dqInput}
                  placeholder="Enter values"
                  value={dqModalDraft.isinValue}
                  onChange={e => setDqModalDraft(d => ({ ...d, isinValue: e.target.value }))}
                />
              </div>
            )}

            {dqModal?.checkType === 'Length Check' && (
              <div className={styles.dqField}>
                <label className={styles.dqLabel}>Value</label>

                <input
                  type="text"
                  className={styles.dqInput}
                  placeholder="Enter values"
                  value={dqModalDraft.lengthValue}
                  onChange={e => setDqModalDraft(d => ({ ...d, lengthValue: e.target.value }))}
                />
              </div>
            )}

            {dqModal?.checkType === 'Format Check' && (
              <div className={styles.dqField}>
                <label className={styles.dqLabel}>Value</label>

                <input
                  type="text"
                  className={styles.dqInput}
                  placeholder="Enter values"
                  value={dqModalDraft.formatValue}
                  onChange={e => setDqModalDraft(d => ({ ...d, formatValue: e.target.value }))}
                />
              </div>
            )}
          </div>

          <div className={styles.dqDialogFooter}>
            <Button
              type="button"
              className={styles.dqDialogSave}
              onClick={handleDqModalSave}
              disabled={!canSaveDqModal}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PrerequisitesModal
        open={prereqModalOpen}
        onOpenChange={setPrereqModalOpen}
        connectorName={connectorMeta?.name}
        prerequisite={prerequisiteData}
      />
    </aside>
  )
}
